import React, { useCallback, useMemo } from 'react';
import { groupBy as groupBy$1, uniq } from 'lodash';
import { ParameterOperator, OrderDirection, ParameterType, MarketLink } from 'betweb-openapi-axios';
import { useTranslation } from 'react-i18next';

const sortProbabilitiesByParamValue = (probabilities) => {
    return [...probabilities].sort((a, b) => {
        var _a, _b, _c, _d;
        const index = Math.min((a === null || a === void 0 ? void 0 : a.parameters.length) - 1, (b === null || b === void 0 ? void 0 : b.parameters.length) - 1);
        const valueA = (_a = a === null || a === void 0 ? void 0 : a.parameters[index]) === null || _a === void 0 ? void 0 : _a.value;
        const valueB = (_b = b === null || b === void 0 ? void 0 : b.parameters[index]) === null || _b === void 0 ? void 0 : _b.value;
        if (!valueA || !valueB) {
            return 0;
        }
        const isWithPlus = [valueA, valueB].some(i => !!/\d\+/gm.exec(i));
        const isCouples = [valueA, valueB].every(i => !!/\d+-\d+/gm.exec(i));
        // if we sorting probabilities by goals_range or time_range
        // we need to parse strings to numbers because they have type like '1-2', '1-45', '15-30', '6+'
        if (isCouples) {
            // we parse string of digit-digit type into a number by addition of the first and the second digit
            // example: '1-2' -> 3 and '15-40' -> 55
            const aTmp = ((_c = valueA === null || valueA === void 0 ? void 0 : valueA.match(/\d+/gm)) === null || _c === void 0 ? void 0 : _c.reduce((acc, cur) => acc + +cur, 0)) || 0;
            const bTmp = ((_d = valueB === null || valueB === void 0 ? void 0 : valueB.match(/\d+/gm)) === null || _d === void 0 ? void 0 : _d.reduce((acc, cur) => acc + +cur, 0)) || 0;
            return aTmp - bTmp;
        }
        if (isWithPlus) {
            // if a`s value has "+" on the end string we sort a after b
            // if b`s value has "+" on the end string we sort b after a
            return /\d\+/gm.exec(valueA) ? 1 : -1;
        }
        return +valueA - +valueB;
    });
};

const OddsTableGroup = ({ probabilities, categoryMarket, transformProbabilities, OddsTableComponent, ...props }) => {
    const filterParameters = useCallback((probability) => probability.parameters.filter(parameter => { var _a; return (_a = categoryMarket.groupedBy) === null || _a === void 0 ? void 0 : _a.includes(parameter.type); }), [categoryMarket]);
    const probabilitiesGroup = useMemo(() => groupBy$1(probabilities, probability => JSON.stringify(filterParameters(probability))), [probabilities, filterParameters]);
    const uniqParamCombinations = useMemo(() => uniq(sortProbabilitiesByParamValue(probabilities)
        .map(probability => filterParameters(probability))
        .map(params => JSON.stringify(params))), [probabilities, filterParameters, sortProbabilitiesByParamValue]);
    return (React.createElement(React.Fragment, null, uniqParamCombinations.map((paramsCombination, index) => (React.createElement(OddsTableComponent, { key: `ParamsCombination-${index}-${paramsCombination}`, rows: transformProbabilities(probabilitiesGroup[paramsCombination]), probabilities: probabilitiesGroup[paramsCombination], categoryMarket: categoryMarket, ...props })))));
};

const isValueMatching = (probabilityParameter, marketParameter) => {
    if (probabilityParameter.type !== marketParameter.type) {
        return false;
    }
    switch (marketParameter.operator) {
        case ParameterOperator.Equal:
            return probabilityParameter.value === marketParameter.value;
        case ParameterOperator.LessThanOrEqual:
            return probabilityParameter.value <= marketParameter.value;
        case ParameterOperator.MoreThanOrEqual:
            return probabilityParameter.value >= marketParameter.value;
        case ParameterOperator.Unknown:
        default:
            return true;
    }
};
const filterProbabilities = (probabilities, categoryMarket) => probabilities.filter(probability => categoryMarket.parameters.every(marketParameter => probability.parameters.some(probabilityParameter => isValueMatching(probabilityParameter, marketParameter))));
// todo write tests
const filterAndSortParameters = (probability, categoryMarket) => {
    if (!categoryMarket.groupedBy)
        return [];
    if (categoryMarket.groupedBy.length === 0)
        return [];
    const filteredParameters = probability.parameters.filter(parameter => { var _a; return (_a = categoryMarket.groupedBy) === null || _a === void 0 ? void 0 : _a.includes(parameter.type); });
    filteredParameters.sort((parameter1, parameter2) => {
        let index1 = 0;
        let index2 = 0;
        if (categoryMarket.groupsOrderedBy) {
            index1 = categoryMarket.groupsOrderedBy.findIndex(el => el.parameterType === parameter1.type);
            index2 = categoryMarket.groupsOrderedBy.findIndex(el => el.parameterType === parameter2.type);
            if (index1 !== index2) {
                if (index1 === -1)
                    return 1;
                return index1 - index2;
            }
        }
        const type1 = parameter1.type;
        const type2 = parameter2.type;
        return type1.localeCompare(type2);
    });
    return filteredParameters;
};
const isNumeric = (value) => Number.isFinite(+value);
const sortByParamOrder = (a, b, order) => {
    var _a, _b;
    if (!order)
        return 0;
    const aV1 = (_a = a.find(el => el.type === order.parameterType)) === null || _a === void 0 ? void 0 : _a.value;
    const bV1 = (_b = b.find(el => el.type === order.parameterType)) === null || _b === void 0 ? void 0 : _b.value;
    if (aV1 === bV1)
        return 0;
    if (aV1 === undefined)
        return 1;
    if (bV1 === undefined)
        return -1;
    if (isNumeric(aV1) && isNumeric(bV1)) {
        if (order.direction === OrderDirection.Asc) {
            return +aV1 - +bV1;
        }
        return +bV1 - +aV1;
    }
    if ([
        ParameterType.GoalsRange,
        ParameterType.PointRange,
        ParameterType.TimeRange,
        ParameterType.Range,
        ParameterType.ExactGoals,
        ParameterType.Exact,
        ParameterType.WinningMargin,
    ].includes(order.parameterType)) {
        // 1-3, 4-6, 6+ range
        // 1 1+, 2 2+ exact, WinningMargin
        let paramValue1Number;
        let paramValue2Number;
        if (isNumeric(aV1)) {
            paramValue1Number = Number(aV1);
        }
        if (isNumeric(bV1)) {
            paramValue2Number = Number(bV1);
        }
        if (aV1.includes('-')) {
            paramValue1Number = Number(aV1.split('-')[0]);
        }
        if (bV1.includes('-')) {
            paramValue2Number = Number(bV1.split('-')[0]);
        }
        if (aV1.includes('+')) {
            paramValue1Number = Number(aV1.split('+')[0]);
        }
        if (bV1.includes('+')) {
            paramValue2Number = Number(bV1.split('+')[0]);
        }
        if (typeof paramValue1Number === 'number' &&
            typeof paramValue2Number === 'number') {
            return order.direction === OrderDirection.Asc
                ? paramValue1Number - paramValue2Number
                : paramValue2Number - paramValue1Number;
        }
    }
    return order.direction === OrderDirection.Asc
        ? aV1.localeCompare(bV1)
        : bV1.localeCompare(aV1);
};
const sortGroups = (_parametersArray, _groupsOrderedBy = []) => {
    const parametersArray = [..._parametersArray];
    const groupsOrderedBy = _groupsOrderedBy ? [..._groupsOrderedBy] : [];
    const [order1, order2, order3] = groupsOrderedBy;
    if (!order1)
        return parametersArray;
    parametersArray.sort((a, b) => {
        let sortFnResult = sortByParamOrder(a, b, order1);
        if (sortFnResult !== 0)
            return sortFnResult;
        sortFnResult = sortByParamOrder(a, b, order2);
        if (sortFnResult !== 0)
            return sortFnResult;
        sortFnResult = sortByParamOrder(a, b, order3);
        if (sortFnResult !== 0)
            return sortFnResult;
        return 0;
    });
    return parametersArray;
};
// todo write tests
const getUniqParamCombinations = (probabilities, categoryMarket) => {
    let parametersArray = probabilities.map(probability => filterAndSortParameters(probability, categoryMarket));
    parametersArray = sortGroups(parametersArray, categoryMarket.groupsOrderedBy);
    return uniq(parametersArray.map(params => JSON.stringify(params)));
};
// todo write tests
const getProbabilitiesGroup = (probabilities, categoryMarket) => {
    return groupBy$1(probabilities, probability => JSON.stringify(filterAndSortParameters(probability, categoryMarket)));
};

const useFilteredProbabilities = (probabilities, categoryMarket) => useMemo(() => filterProbabilities(probabilities, categoryMarket), [probabilities, categoryMarket]);

/**
 * Appends the elements of `values` to `array`.
 *
 * @private
 * @param {Array} array The array to modify.
 * @param {Array} values The values to append.
 * @returns {Array} Returns `array`.
 */
function arrayPush(array, values) {
  var index = -1,
      length = values.length,
      offset = array.length;

  while (++index < length) {
    array[offset + index] = values[index];
  }
  return array;
}

var _arrayPush = arrayPush;

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

var _freeGlobal = freeGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = _freeGlobal || freeSelf || Function('return this')();

var _root = root;

/** Built-in value references. */
var Symbol = _root.Symbol;

var _Symbol = Symbol;

/** Used for built-in method references. */
var objectProto = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto.toString;

/** Built-in value references. */
var symToStringTag = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag(value) {
  var isOwn = hasOwnProperty.call(value, symToStringTag),
      tag = value[symToStringTag];

  try {
    value[symToStringTag] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag] = tag;
    } else {
      delete value[symToStringTag];
    }
  }
  return result;
}

var _getRawTag = getRawTag;

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$1.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString(value) {
  return nativeObjectToString$1.call(value);
}

var _objectToString = objectToString;

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag$1 = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag$1 && symToStringTag$1 in Object(value))
    ? _getRawTag(value)
    : _objectToString(value);
}

var _baseGetTag = baseGetTag;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1 = isObjectLike;

/** `Object#toString` result references. */
var argsTag = '[object Arguments]';

/**
 * The base implementation of `_.isArguments`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 */
function baseIsArguments(value) {
  return isObjectLike_1(value) && _baseGetTag(value) == argsTag;
}

var _baseIsArguments = baseIsArguments;

/** Used for built-in method references. */
var objectProto$2 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto$2.hasOwnProperty;

/** Built-in value references. */
var propertyIsEnumerable = objectProto$2.propertyIsEnumerable;

/**
 * Checks if `value` is likely an `arguments` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 *  else `false`.
 * @example
 *
 * _.isArguments(function() { return arguments; }());
 * // => true
 *
 * _.isArguments([1, 2, 3]);
 * // => false
 */
var isArguments = _baseIsArguments(function() { return arguments; }()) ? _baseIsArguments : function(value) {
  return isObjectLike_1(value) && hasOwnProperty$1.call(value, 'callee') &&
    !propertyIsEnumerable.call(value, 'callee');
};

var isArguments_1 = isArguments;

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */
var isArray = Array.isArray;

var isArray_1 = isArray;

/** Built-in value references. */
var spreadableSymbol = _Symbol ? _Symbol.isConcatSpreadable : undefined;

/**
 * Checks if `value` is a flattenable `arguments` object or array.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is flattenable, else `false`.
 */
function isFlattenable(value) {
  return isArray_1(value) || isArguments_1(value) ||
    !!(spreadableSymbol && value && value[spreadableSymbol]);
}

var _isFlattenable = isFlattenable;

/**
 * The base implementation of `_.flatten` with support for restricting flattening.
 *
 * @private
 * @param {Array} array The array to flatten.
 * @param {number} depth The maximum recursion depth.
 * @param {boolean} [predicate=isFlattenable] The function invoked per iteration.
 * @param {boolean} [isStrict] Restrict to values that pass `predicate` checks.
 * @param {Array} [result=[]] The initial result value.
 * @returns {Array} Returns the new flattened array.
 */
function baseFlatten(array, depth, predicate, isStrict, result) {
  var index = -1,
      length = array.length;

  predicate || (predicate = _isFlattenable);
  result || (result = []);

  while (++index < length) {
    var value = array[index];
    if (depth > 0 && predicate(value)) {
      if (depth > 1) {
        // Recursively flatten arrays (susceptible to call stack limits).
        baseFlatten(value, depth - 1, predicate, isStrict, result);
      } else {
        _arrayPush(result, value);
      }
    } else if (!isStrict) {
      result[result.length] = value;
    }
  }
  return result;
}

var _baseFlatten = baseFlatten;

/**
 * A specialized version of `_.map` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */
function arrayMap(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length,
      result = Array(length);

  while (++index < length) {
    result[index] = iteratee(array[index], index, array);
  }
  return result;
}

var _arrayMap = arrayMap;

/** `Object#toString` result references. */
var symbolTag = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol(value) {
  return typeof value == 'symbol' ||
    (isObjectLike_1(value) && _baseGetTag(value) == symbolTag);
}

var isSymbol_1 = isSymbol;

/** Used to match property names within property paths. */
var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/,
    reIsPlainProp = /^\w*$/;

/**
 * Checks if `value` is a property name and not a property path.
 *
 * @private
 * @param {*} value The value to check.
 * @param {Object} [object] The object to query keys on.
 * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
 */
function isKey(value, object) {
  if (isArray_1(value)) {
    return false;
  }
  var type = typeof value;
  if (type == 'number' || type == 'symbol' || type == 'boolean' ||
      value == null || isSymbol_1(value)) {
    return true;
  }
  return reIsPlainProp.test(value) || !reIsDeepProp.test(value) ||
    (object != null && value in Object(object));
}

var _isKey = isKey;

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var isObject_1 = isObject;

/** `Object#toString` result references. */
var asyncTag = '[object AsyncFunction]',
    funcTag = '[object Function]',
    genTag = '[object GeneratorFunction]',
    proxyTag = '[object Proxy]';

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction(value) {
  if (!isObject_1(value)) {
    return false;
  }
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 9 which returns 'object' for typed arrays and other constructors.
  var tag = _baseGetTag(value);
  return tag == funcTag || tag == genTag || tag == asyncTag || tag == proxyTag;
}

var isFunction_1 = isFunction;

/** Used to detect overreaching core-js shims. */
var coreJsData = _root['__core-js_shared__'];

var _coreJsData = coreJsData;

/** Used to detect methods masquerading as native. */
var maskSrcKey = (function() {
  var uid = /[^.]+$/.exec(_coreJsData && _coreJsData.keys && _coreJsData.keys.IE_PROTO || '');
  return uid ? ('Symbol(src)_1.' + uid) : '';
}());

/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */
function isMasked(func) {
  return !!maskSrcKey && (maskSrcKey in func);
}

var _isMasked = isMasked;

/** Used for built-in method references. */
var funcProto = Function.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to convert.
 * @returns {string} Returns the source code.
 */
function toSource(func) {
  if (func != null) {
    try {
      return funcToString.call(func);
    } catch (e) {}
    try {
      return (func + '');
    } catch (e) {}
  }
  return '';
}

var _toSource = toSource;

/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */
var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;

/** Used to detect host constructors (Safari). */
var reIsHostCtor = /^\[object .+?Constructor\]$/;

/** Used for built-in method references. */
var funcProto$1 = Function.prototype,
    objectProto$3 = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString$1 = funcProto$1.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$2 = objectProto$3.hasOwnProperty;

/** Used to detect if a method is native. */
var reIsNative = RegExp('^' +
  funcToString$1.call(hasOwnProperty$2).replace(reRegExpChar, '\\$&')
  .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
);

/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */
function baseIsNative(value) {
  if (!isObject_1(value) || _isMasked(value)) {
    return false;
  }
  var pattern = isFunction_1(value) ? reIsNative : reIsHostCtor;
  return pattern.test(_toSource(value));
}

var _baseIsNative = baseIsNative;

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */
function getValue(object, key) {
  return object == null ? undefined : object[key];
}

var _getValue = getValue;

/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */
function getNative(object, key) {
  var value = _getValue(object, key);
  return _baseIsNative(value) ? value : undefined;
}

var _getNative = getNative;

/* Built-in method references that are verified to be native. */
var nativeCreate = _getNative(Object, 'create');

var _nativeCreate = nativeCreate;

/**
 * Removes all key-value entries from the hash.
 *
 * @private
 * @name clear
 * @memberOf Hash
 */
function hashClear() {
  this.__data__ = _nativeCreate ? _nativeCreate(null) : {};
  this.size = 0;
}

var _hashClear = hashClear;

/**
 * Removes `key` and its value from the hash.
 *
 * @private
 * @name delete
 * @memberOf Hash
 * @param {Object} hash The hash to modify.
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function hashDelete(key) {
  var result = this.has(key) && delete this.__data__[key];
  this.size -= result ? 1 : 0;
  return result;
}

var _hashDelete = hashDelete;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED = '__lodash_hash_undefined__';

/** Used for built-in method references. */
var objectProto$4 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$3 = objectProto$4.hasOwnProperty;

/**
 * Gets the hash value for `key`.
 *
 * @private
 * @name get
 * @memberOf Hash
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function hashGet(key) {
  var data = this.__data__;
  if (_nativeCreate) {
    var result = data[key];
    return result === HASH_UNDEFINED ? undefined : result;
  }
  return hasOwnProperty$3.call(data, key) ? data[key] : undefined;
}

var _hashGet = hashGet;

/** Used for built-in method references. */
var objectProto$5 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$4 = objectProto$5.hasOwnProperty;

/**
 * Checks if a hash value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Hash
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function hashHas(key) {
  var data = this.__data__;
  return _nativeCreate ? (data[key] !== undefined) : hasOwnProperty$4.call(data, key);
}

var _hashHas = hashHas;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$1 = '__lodash_hash_undefined__';

/**
 * Sets the hash `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Hash
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the hash instance.
 */
function hashSet(key, value) {
  var data = this.__data__;
  this.size += this.has(key) ? 0 : 1;
  data[key] = (_nativeCreate && value === undefined) ? HASH_UNDEFINED$1 : value;
  return this;
}

var _hashSet = hashSet;

/**
 * Creates a hash object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Hash(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `Hash`.
Hash.prototype.clear = _hashClear;
Hash.prototype['delete'] = _hashDelete;
Hash.prototype.get = _hashGet;
Hash.prototype.has = _hashHas;
Hash.prototype.set = _hashSet;

var _Hash = Hash;

/**
 * Removes all key-value entries from the list cache.
 *
 * @private
 * @name clear
 * @memberOf ListCache
 */
function listCacheClear() {
  this.__data__ = [];
  this.size = 0;
}

var _listCacheClear = listCacheClear;

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */
function eq(value, other) {
  return value === other || (value !== value && other !== other);
}

var eq_1 = eq;

/**
 * Gets the index at which the `key` is found in `array` of key-value pairs.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} key The key to search for.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function assocIndexOf(array, key) {
  var length = array.length;
  while (length--) {
    if (eq_1(array[length][0], key)) {
      return length;
    }
  }
  return -1;
}

var _assocIndexOf = assocIndexOf;

/** Used for built-in method references. */
var arrayProto = Array.prototype;

/** Built-in value references. */
var splice = arrayProto.splice;

/**
 * Removes `key` and its value from the list cache.
 *
 * @private
 * @name delete
 * @memberOf ListCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function listCacheDelete(key) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  if (index < 0) {
    return false;
  }
  var lastIndex = data.length - 1;
  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }
  --this.size;
  return true;
}

var _listCacheDelete = listCacheDelete;

/**
 * Gets the list cache value for `key`.
 *
 * @private
 * @name get
 * @memberOf ListCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function listCacheGet(key) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  return index < 0 ? undefined : data[index][1];
}

var _listCacheGet = listCacheGet;

/**
 * Checks if a list cache value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf ListCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function listCacheHas(key) {
  return _assocIndexOf(this.__data__, key) > -1;
}

var _listCacheHas = listCacheHas;

/**
 * Sets the list cache `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf ListCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the list cache instance.
 */
function listCacheSet(key, value) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  if (index < 0) {
    ++this.size;
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }
  return this;
}

var _listCacheSet = listCacheSet;

/**
 * Creates an list cache object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function ListCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `ListCache`.
ListCache.prototype.clear = _listCacheClear;
ListCache.prototype['delete'] = _listCacheDelete;
ListCache.prototype.get = _listCacheGet;
ListCache.prototype.has = _listCacheHas;
ListCache.prototype.set = _listCacheSet;

var _ListCache = ListCache;

/* Built-in method references that are verified to be native. */
var Map = _getNative(_root, 'Map');

var _Map = Map;

/**
 * Removes all key-value entries from the map.
 *
 * @private
 * @name clear
 * @memberOf MapCache
 */
function mapCacheClear() {
  this.size = 0;
  this.__data__ = {
    'hash': new _Hash,
    'map': new (_Map || _ListCache),
    'string': new _Hash
  };
}

var _mapCacheClear = mapCacheClear;

/**
 * Checks if `value` is suitable for use as unique object key.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
 */
function isKeyable(value) {
  var type = typeof value;
  return (type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean')
    ? (value !== '__proto__')
    : (value === null);
}

var _isKeyable = isKeyable;

/**
 * Gets the data for `map`.
 *
 * @private
 * @param {Object} map The map to query.
 * @param {string} key The reference key.
 * @returns {*} Returns the map data.
 */
function getMapData(map, key) {
  var data = map.__data__;
  return _isKeyable(key)
    ? data[typeof key == 'string' ? 'string' : 'hash']
    : data.map;
}

var _getMapData = getMapData;

/**
 * Removes `key` and its value from the map.
 *
 * @private
 * @name delete
 * @memberOf MapCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function mapCacheDelete(key) {
  var result = _getMapData(this, key)['delete'](key);
  this.size -= result ? 1 : 0;
  return result;
}

var _mapCacheDelete = mapCacheDelete;

/**
 * Gets the map value for `key`.
 *
 * @private
 * @name get
 * @memberOf MapCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function mapCacheGet(key) {
  return _getMapData(this, key).get(key);
}

var _mapCacheGet = mapCacheGet;

/**
 * Checks if a map value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf MapCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function mapCacheHas(key) {
  return _getMapData(this, key).has(key);
}

var _mapCacheHas = mapCacheHas;

/**
 * Sets the map `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf MapCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the map cache instance.
 */
function mapCacheSet(key, value) {
  var data = _getMapData(this, key),
      size = data.size;

  data.set(key, value);
  this.size += data.size == size ? 0 : 1;
  return this;
}

var _mapCacheSet = mapCacheSet;

/**
 * Creates a map cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function MapCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `MapCache`.
MapCache.prototype.clear = _mapCacheClear;
MapCache.prototype['delete'] = _mapCacheDelete;
MapCache.prototype.get = _mapCacheGet;
MapCache.prototype.has = _mapCacheHas;
MapCache.prototype.set = _mapCacheSet;

var _MapCache = MapCache;

/** Error message constants. */
var FUNC_ERROR_TEXT = 'Expected a function';

/**
 * Creates a function that memoizes the result of `func`. If `resolver` is
 * provided, it determines the cache key for storing the result based on the
 * arguments provided to the memoized function. By default, the first argument
 * provided to the memoized function is used as the map cache key. The `func`
 * is invoked with the `this` binding of the memoized function.
 *
 * **Note:** The cache is exposed as the `cache` property on the memoized
 * function. Its creation may be customized by replacing the `_.memoize.Cache`
 * constructor with one whose instances implement the
 * [`Map`](http://ecma-international.org/ecma-262/7.0/#sec-properties-of-the-map-prototype-object)
 * method interface of `clear`, `delete`, `get`, `has`, and `set`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to have its output memoized.
 * @param {Function} [resolver] The function to resolve the cache key.
 * @returns {Function} Returns the new memoized function.
 * @example
 *
 * var object = { 'a': 1, 'b': 2 };
 * var other = { 'c': 3, 'd': 4 };
 *
 * var values = _.memoize(_.values);
 * values(object);
 * // => [1, 2]
 *
 * values(other);
 * // => [3, 4]
 *
 * object.a = 2;
 * values(object);
 * // => [1, 2]
 *
 * // Modify the result cache.
 * values.cache.set(object, ['a', 'b']);
 * values(object);
 * // => ['a', 'b']
 *
 * // Replace `_.memoize.Cache`.
 * _.memoize.Cache = WeakMap;
 */
function memoize(func, resolver) {
  if (typeof func != 'function' || (resolver != null && typeof resolver != 'function')) {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  var memoized = function() {
    var args = arguments,
        key = resolver ? resolver.apply(this, args) : args[0],
        cache = memoized.cache;

    if (cache.has(key)) {
      return cache.get(key);
    }
    var result = func.apply(this, args);
    memoized.cache = cache.set(key, result) || cache;
    return result;
  };
  memoized.cache = new (memoize.Cache || _MapCache);
  return memoized;
}

// Expose `MapCache`.
memoize.Cache = _MapCache;

var memoize_1 = memoize;

/** Used as the maximum memoize cache size. */
var MAX_MEMOIZE_SIZE = 500;

/**
 * A specialized version of `_.memoize` which clears the memoized function's
 * cache when it exceeds `MAX_MEMOIZE_SIZE`.
 *
 * @private
 * @param {Function} func The function to have its output memoized.
 * @returns {Function} Returns the new memoized function.
 */
function memoizeCapped(func) {
  var result = memoize_1(func, function(key) {
    if (cache.size === MAX_MEMOIZE_SIZE) {
      cache.clear();
    }
    return key;
  });

  var cache = result.cache;
  return result;
}

var _memoizeCapped = memoizeCapped;

/** Used to match property names within property paths. */
var rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;

/** Used to match backslashes in property paths. */
var reEscapeChar = /\\(\\)?/g;

/**
 * Converts `string` to a property path array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the property path array.
 */
var stringToPath = _memoizeCapped(function(string) {
  var result = [];
  if (string.charCodeAt(0) === 46 /* . */) {
    result.push('');
  }
  string.replace(rePropName, function(match, number, quote, subString) {
    result.push(quote ? subString.replace(reEscapeChar, '$1') : (number || match));
  });
  return result;
});

var _stringToPath = stringToPath;

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0;

/** Used to convert symbols to primitives and strings. */
var symbolProto = _Symbol ? _Symbol.prototype : undefined,
    symbolToString = symbolProto ? symbolProto.toString : undefined;

/**
 * The base implementation of `_.toString` which doesn't convert nullish
 * values to empty strings.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */
function baseToString(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value;
  }
  if (isArray_1(value)) {
    // Recursively convert values (susceptible to call stack limits).
    return _arrayMap(value, baseToString) + '';
  }
  if (isSymbol_1(value)) {
    return symbolToString ? symbolToString.call(value) : '';
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
}

var _baseToString = baseToString;

/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 * @example
 *
 * _.toString(null);
 * // => ''
 *
 * _.toString(-0);
 * // => '-0'
 *
 * _.toString([1, 2, 3]);
 * // => '1,2,3'
 */
function toString(value) {
  return value == null ? '' : _baseToString(value);
}

var toString_1 = toString;

/**
 * Casts `value` to a path array if it's not one.
 *
 * @private
 * @param {*} value The value to inspect.
 * @param {Object} [object] The object to query keys on.
 * @returns {Array} Returns the cast property path array.
 */
function castPath(value, object) {
  if (isArray_1(value)) {
    return value;
  }
  return _isKey(value, object) ? [value] : _stringToPath(toString_1(value));
}

var _castPath = castPath;

/** Used as references for various `Number` constants. */
var INFINITY$1 = 1 / 0;

/**
 * Converts `value` to a string key if it's not a string or symbol.
 *
 * @private
 * @param {*} value The value to inspect.
 * @returns {string|symbol} Returns the key.
 */
function toKey(value) {
  if (typeof value == 'string' || isSymbol_1(value)) {
    return value;
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY$1) ? '-0' : result;
}

var _toKey = toKey;

/**
 * The base implementation of `_.get` without support for default values.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @returns {*} Returns the resolved value.
 */
function baseGet(object, path) {
  path = _castPath(path, object);

  var index = 0,
      length = path.length;

  while (object != null && index < length) {
    object = object[_toKey(path[index++])];
  }
  return (index && index == length) ? object : undefined;
}

var _baseGet = baseGet;

/**
 * Removes all key-value entries from the stack.
 *
 * @private
 * @name clear
 * @memberOf Stack
 */
function stackClear() {
  this.__data__ = new _ListCache;
  this.size = 0;
}

var _stackClear = stackClear;

/**
 * Removes `key` and its value from the stack.
 *
 * @private
 * @name delete
 * @memberOf Stack
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function stackDelete(key) {
  var data = this.__data__,
      result = data['delete'](key);

  this.size = data.size;
  return result;
}

var _stackDelete = stackDelete;

/**
 * Gets the stack value for `key`.
 *
 * @private
 * @name get
 * @memberOf Stack
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function stackGet(key) {
  return this.__data__.get(key);
}

var _stackGet = stackGet;

/**
 * Checks if a stack value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Stack
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function stackHas(key) {
  return this.__data__.has(key);
}

var _stackHas = stackHas;

/** Used as the size to enable large array optimizations. */
var LARGE_ARRAY_SIZE = 200;

/**
 * Sets the stack `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Stack
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the stack cache instance.
 */
function stackSet(key, value) {
  var data = this.__data__;
  if (data instanceof _ListCache) {
    var pairs = data.__data__;
    if (!_Map || (pairs.length < LARGE_ARRAY_SIZE - 1)) {
      pairs.push([key, value]);
      this.size = ++data.size;
      return this;
    }
    data = this.__data__ = new _MapCache(pairs);
  }
  data.set(key, value);
  this.size = data.size;
  return this;
}

var _stackSet = stackSet;

/**
 * Creates a stack cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Stack(entries) {
  var data = this.__data__ = new _ListCache(entries);
  this.size = data.size;
}

// Add methods to `Stack`.
Stack.prototype.clear = _stackClear;
Stack.prototype['delete'] = _stackDelete;
Stack.prototype.get = _stackGet;
Stack.prototype.has = _stackHas;
Stack.prototype.set = _stackSet;

var _Stack = Stack;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$2 = '__lodash_hash_undefined__';

/**
 * Adds `value` to the array cache.
 *
 * @private
 * @name add
 * @memberOf SetCache
 * @alias push
 * @param {*} value The value to cache.
 * @returns {Object} Returns the cache instance.
 */
function setCacheAdd(value) {
  this.__data__.set(value, HASH_UNDEFINED$2);
  return this;
}

var _setCacheAdd = setCacheAdd;

/**
 * Checks if `value` is in the array cache.
 *
 * @private
 * @name has
 * @memberOf SetCache
 * @param {*} value The value to search for.
 * @returns {number} Returns `true` if `value` is found, else `false`.
 */
function setCacheHas(value) {
  return this.__data__.has(value);
}

var _setCacheHas = setCacheHas;

/**
 *
 * Creates an array cache object to store unique values.
 *
 * @private
 * @constructor
 * @param {Array} [values] The values to cache.
 */
function SetCache(values) {
  var index = -1,
      length = values == null ? 0 : values.length;

  this.__data__ = new _MapCache;
  while (++index < length) {
    this.add(values[index]);
  }
}

// Add methods to `SetCache`.
SetCache.prototype.add = SetCache.prototype.push = _setCacheAdd;
SetCache.prototype.has = _setCacheHas;

var _SetCache = SetCache;

/**
 * A specialized version of `_.some` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {boolean} Returns `true` if any element passes the predicate check,
 *  else `false`.
 */
function arraySome(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (predicate(array[index], index, array)) {
      return true;
    }
  }
  return false;
}

var _arraySome = arraySome;

/**
 * Checks if a `cache` value for `key` exists.
 *
 * @private
 * @param {Object} cache The cache to query.
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function cacheHas(cache, key) {
  return cache.has(key);
}

var _cacheHas = cacheHas;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG = 1,
    COMPARE_UNORDERED_FLAG = 2;

/**
 * A specialized version of `baseIsEqualDeep` for arrays with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Array} array The array to compare.
 * @param {Array} other The other array to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `array` and `other` objects.
 * @returns {boolean} Returns `true` if the arrays are equivalent, else `false`.
 */
function equalArrays(array, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG,
      arrLength = array.length,
      othLength = other.length;

  if (arrLength != othLength && !(isPartial && othLength > arrLength)) {
    return false;
  }
  // Check that cyclic values are equal.
  var arrStacked = stack.get(array);
  var othStacked = stack.get(other);
  if (arrStacked && othStacked) {
    return arrStacked == other && othStacked == array;
  }
  var index = -1,
      result = true,
      seen = (bitmask & COMPARE_UNORDERED_FLAG) ? new _SetCache : undefined;

  stack.set(array, other);
  stack.set(other, array);

  // Ignore non-index properties.
  while (++index < arrLength) {
    var arrValue = array[index],
        othValue = other[index];

    if (customizer) {
      var compared = isPartial
        ? customizer(othValue, arrValue, index, other, array, stack)
        : customizer(arrValue, othValue, index, array, other, stack);
    }
    if (compared !== undefined) {
      if (compared) {
        continue;
      }
      result = false;
      break;
    }
    // Recursively compare arrays (susceptible to call stack limits).
    if (seen) {
      if (!_arraySome(other, function(othValue, othIndex) {
            if (!_cacheHas(seen, othIndex) &&
                (arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
              return seen.push(othIndex);
            }
          })) {
        result = false;
        break;
      }
    } else if (!(
          arrValue === othValue ||
            equalFunc(arrValue, othValue, bitmask, customizer, stack)
        )) {
      result = false;
      break;
    }
  }
  stack['delete'](array);
  stack['delete'](other);
  return result;
}

var _equalArrays = equalArrays;

/** Built-in value references. */
var Uint8Array = _root.Uint8Array;

var _Uint8Array = Uint8Array;

/**
 * Converts `map` to its key-value pairs.
 *
 * @private
 * @param {Object} map The map to convert.
 * @returns {Array} Returns the key-value pairs.
 */
function mapToArray(map) {
  var index = -1,
      result = Array(map.size);

  map.forEach(function(value, key) {
    result[++index] = [key, value];
  });
  return result;
}

var _mapToArray = mapToArray;

/**
 * Converts `set` to an array of its values.
 *
 * @private
 * @param {Object} set The set to convert.
 * @returns {Array} Returns the values.
 */
function setToArray(set) {
  var index = -1,
      result = Array(set.size);

  set.forEach(function(value) {
    result[++index] = value;
  });
  return result;
}

var _setToArray = setToArray;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$1 = 1,
    COMPARE_UNORDERED_FLAG$1 = 2;

/** `Object#toString` result references. */
var boolTag = '[object Boolean]',
    dateTag = '[object Date]',
    errorTag = '[object Error]',
    mapTag = '[object Map]',
    numberTag = '[object Number]',
    regexpTag = '[object RegExp]',
    setTag = '[object Set]',
    stringTag = '[object String]',
    symbolTag$1 = '[object Symbol]';

var arrayBufferTag = '[object ArrayBuffer]',
    dataViewTag = '[object DataView]';

/** Used to convert symbols to primitives and strings. */
var symbolProto$1 = _Symbol ? _Symbol.prototype : undefined,
    symbolValueOf = symbolProto$1 ? symbolProto$1.valueOf : undefined;

/**
 * A specialized version of `baseIsEqualDeep` for comparing objects of
 * the same `toStringTag`.
 *
 * **Note:** This function only supports comparing values with tags of
 * `Boolean`, `Date`, `Error`, `Number`, `RegExp`, or `String`.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {string} tag The `toStringTag` of the objects to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function equalByTag(object, other, tag, bitmask, customizer, equalFunc, stack) {
  switch (tag) {
    case dataViewTag:
      if ((object.byteLength != other.byteLength) ||
          (object.byteOffset != other.byteOffset)) {
        return false;
      }
      object = object.buffer;
      other = other.buffer;

    case arrayBufferTag:
      if ((object.byteLength != other.byteLength) ||
          !equalFunc(new _Uint8Array(object), new _Uint8Array(other))) {
        return false;
      }
      return true;

    case boolTag:
    case dateTag:
    case numberTag:
      // Coerce booleans to `1` or `0` and dates to milliseconds.
      // Invalid dates are coerced to `NaN`.
      return eq_1(+object, +other);

    case errorTag:
      return object.name == other.name && object.message == other.message;

    case regexpTag:
    case stringTag:
      // Coerce regexes to strings and treat strings, primitives and objects,
      // as equal. See http://www.ecma-international.org/ecma-262/7.0/#sec-regexp.prototype.tostring
      // for more details.
      return object == (other + '');

    case mapTag:
      var convert = _mapToArray;

    case setTag:
      var isPartial = bitmask & COMPARE_PARTIAL_FLAG$1;
      convert || (convert = _setToArray);

      if (object.size != other.size && !isPartial) {
        return false;
      }
      // Assume cyclic values are equal.
      var stacked = stack.get(object);
      if (stacked) {
        return stacked == other;
      }
      bitmask |= COMPARE_UNORDERED_FLAG$1;

      // Recursively compare objects (susceptible to call stack limits).
      stack.set(object, other);
      var result = _equalArrays(convert(object), convert(other), bitmask, customizer, equalFunc, stack);
      stack['delete'](object);
      return result;

    case symbolTag$1:
      if (symbolValueOf) {
        return symbolValueOf.call(object) == symbolValueOf.call(other);
      }
  }
  return false;
}

var _equalByTag = equalByTag;

/**
 * The base implementation of `getAllKeys` and `getAllKeysIn` which uses
 * `keysFunc` and `symbolsFunc` to get the enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @param {Function} symbolsFunc The function to get the symbols of `object`.
 * @returns {Array} Returns the array of property names and symbols.
 */
function baseGetAllKeys(object, keysFunc, symbolsFunc) {
  var result = keysFunc(object);
  return isArray_1(object) ? result : _arrayPush(result, symbolsFunc(object));
}

var _baseGetAllKeys = baseGetAllKeys;

/**
 * A specialized version of `_.filter` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 */
function arrayFilter(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length,
      resIndex = 0,
      result = [];

  while (++index < length) {
    var value = array[index];
    if (predicate(value, index, array)) {
      result[resIndex++] = value;
    }
  }
  return result;
}

var _arrayFilter = arrayFilter;

/**
 * This method returns a new empty array.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {Array} Returns the new empty array.
 * @example
 *
 * var arrays = _.times(2, _.stubArray);
 *
 * console.log(arrays);
 * // => [[], []]
 *
 * console.log(arrays[0] === arrays[1]);
 * // => false
 */
function stubArray() {
  return [];
}

var stubArray_1 = stubArray;

/** Used for built-in method references. */
var objectProto$6 = Object.prototype;

/** Built-in value references. */
var propertyIsEnumerable$1 = objectProto$6.propertyIsEnumerable;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeGetSymbols = Object.getOwnPropertySymbols;

/**
 * Creates an array of the own enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */
var getSymbols = !nativeGetSymbols ? stubArray_1 : function(object) {
  if (object == null) {
    return [];
  }
  object = Object(object);
  return _arrayFilter(nativeGetSymbols(object), function(symbol) {
    return propertyIsEnumerable$1.call(object, symbol);
  });
};

var _getSymbols = getSymbols;

/**
 * The base implementation of `_.times` without support for iteratee shorthands
 * or max array length checks.
 *
 * @private
 * @param {number} n The number of times to invoke `iteratee`.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the array of results.
 */
function baseTimes(n, iteratee) {
  var index = -1,
      result = Array(n);

  while (++index < n) {
    result[index] = iteratee(index);
  }
  return result;
}

var _baseTimes = baseTimes;

/**
 * This method returns `false`.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {boolean} Returns `false`.
 * @example
 *
 * _.times(2, _.stubFalse);
 * // => [false, false]
 */
function stubFalse() {
  return false;
}

var stubFalse_1 = stubFalse;

var isBuffer_1 = createCommonjsModule(function (module, exports) {
/** Detect free variable `exports`. */
var freeExports =  exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Built-in value references. */
var Buffer = moduleExports ? _root.Buffer : undefined;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined;

/**
 * Checks if `value` is a buffer.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
 * @example
 *
 * _.isBuffer(new Buffer(2));
 * // => true
 *
 * _.isBuffer(new Uint8Array(2));
 * // => false
 */
var isBuffer = nativeIsBuffer || stubFalse_1;

module.exports = isBuffer;
});

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER = 9007199254740991;

/** Used to detect unsigned integer values. */
var reIsUint = /^(?:0|[1-9]\d*)$/;

/**
 * Checks if `value` is a valid array-like index.
 *
 * @private
 * @param {*} value The value to check.
 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
 */
function isIndex(value, length) {
  var type = typeof value;
  length = length == null ? MAX_SAFE_INTEGER : length;

  return !!length &&
    (type == 'number' ||
      (type != 'symbol' && reIsUint.test(value))) &&
        (value > -1 && value % 1 == 0 && value < length);
}

var _isIndex = isIndex;

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER$1 = 9007199254740991;

/**
 * Checks if `value` is a valid array-like length.
 *
 * **Note:** This method is loosely based on
 * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
 * @example
 *
 * _.isLength(3);
 * // => true
 *
 * _.isLength(Number.MIN_VALUE);
 * // => false
 *
 * _.isLength(Infinity);
 * // => false
 *
 * _.isLength('3');
 * // => false
 */
function isLength(value) {
  return typeof value == 'number' &&
    value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER$1;
}

var isLength_1 = isLength;

/** `Object#toString` result references. */
var argsTag$1 = '[object Arguments]',
    arrayTag = '[object Array]',
    boolTag$1 = '[object Boolean]',
    dateTag$1 = '[object Date]',
    errorTag$1 = '[object Error]',
    funcTag$1 = '[object Function]',
    mapTag$1 = '[object Map]',
    numberTag$1 = '[object Number]',
    objectTag = '[object Object]',
    regexpTag$1 = '[object RegExp]',
    setTag$1 = '[object Set]',
    stringTag$1 = '[object String]',
    weakMapTag = '[object WeakMap]';

var arrayBufferTag$1 = '[object ArrayBuffer]',
    dataViewTag$1 = '[object DataView]',
    float32Tag = '[object Float32Array]',
    float64Tag = '[object Float64Array]',
    int8Tag = '[object Int8Array]',
    int16Tag = '[object Int16Array]',
    int32Tag = '[object Int32Array]',
    uint8Tag = '[object Uint8Array]',
    uint8ClampedTag = '[object Uint8ClampedArray]',
    uint16Tag = '[object Uint16Array]',
    uint32Tag = '[object Uint32Array]';

/** Used to identify `toStringTag` values of typed arrays. */
var typedArrayTags = {};
typedArrayTags[float32Tag] = typedArrayTags[float64Tag] =
typedArrayTags[int8Tag] = typedArrayTags[int16Tag] =
typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] =
typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] =
typedArrayTags[uint32Tag] = true;
typedArrayTags[argsTag$1] = typedArrayTags[arrayTag] =
typedArrayTags[arrayBufferTag$1] = typedArrayTags[boolTag$1] =
typedArrayTags[dataViewTag$1] = typedArrayTags[dateTag$1] =
typedArrayTags[errorTag$1] = typedArrayTags[funcTag$1] =
typedArrayTags[mapTag$1] = typedArrayTags[numberTag$1] =
typedArrayTags[objectTag] = typedArrayTags[regexpTag$1] =
typedArrayTags[setTag$1] = typedArrayTags[stringTag$1] =
typedArrayTags[weakMapTag] = false;

/**
 * The base implementation of `_.isTypedArray` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 */
function baseIsTypedArray(value) {
  return isObjectLike_1(value) &&
    isLength_1(value.length) && !!typedArrayTags[_baseGetTag(value)];
}

var _baseIsTypedArray = baseIsTypedArray;

/**
 * The base implementation of `_.unary` without support for storing metadata.
 *
 * @private
 * @param {Function} func The function to cap arguments for.
 * @returns {Function} Returns the new capped function.
 */
function baseUnary(func) {
  return function(value) {
    return func(value);
  };
}

var _baseUnary = baseUnary;

var _nodeUtil = createCommonjsModule(function (module, exports) {
/** Detect free variable `exports`. */
var freeExports =  exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Detect free variable `process` from Node.js. */
var freeProcess = moduleExports && _freeGlobal.process;

/** Used to access faster Node.js helpers. */
var nodeUtil = (function() {
  try {
    // Use `util.types` for Node.js 10+.
    var types = freeModule && freeModule.require && freeModule.require('util').types;

    if (types) {
      return types;
    }

    // Legacy `process.binding('util')` for Node.js < 10.
    return freeProcess && freeProcess.binding && freeProcess.binding('util');
  } catch (e) {}
}());

module.exports = nodeUtil;
});

/* Node.js helper references. */
var nodeIsTypedArray = _nodeUtil && _nodeUtil.isTypedArray;

/**
 * Checks if `value` is classified as a typed array.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 * @example
 *
 * _.isTypedArray(new Uint8Array);
 * // => true
 *
 * _.isTypedArray([]);
 * // => false
 */
var isTypedArray = nodeIsTypedArray ? _baseUnary(nodeIsTypedArray) : _baseIsTypedArray;

var isTypedArray_1 = isTypedArray;

/** Used for built-in method references. */
var objectProto$7 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$5 = objectProto$7.hasOwnProperty;

/**
 * Creates an array of the enumerable property names of the array-like `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @param {boolean} inherited Specify returning inherited property names.
 * @returns {Array} Returns the array of property names.
 */
function arrayLikeKeys(value, inherited) {
  var isArr = isArray_1(value),
      isArg = !isArr && isArguments_1(value),
      isBuff = !isArr && !isArg && isBuffer_1(value),
      isType = !isArr && !isArg && !isBuff && isTypedArray_1(value),
      skipIndexes = isArr || isArg || isBuff || isType,
      result = skipIndexes ? _baseTimes(value.length, String) : [],
      length = result.length;

  for (var key in value) {
    if ((inherited || hasOwnProperty$5.call(value, key)) &&
        !(skipIndexes && (
           // Safari 9 has enumerable `arguments.length` in strict mode.
           key == 'length' ||
           // Node.js 0.10 has enumerable non-index properties on buffers.
           (isBuff && (key == 'offset' || key == 'parent')) ||
           // PhantomJS 2 has enumerable non-index properties on typed arrays.
           (isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset')) ||
           // Skip index properties.
           _isIndex(key, length)
        ))) {
      result.push(key);
    }
  }
  return result;
}

var _arrayLikeKeys = arrayLikeKeys;

/** Used for built-in method references. */
var objectProto$8 = Object.prototype;

/**
 * Checks if `value` is likely a prototype object.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
 */
function isPrototype(value) {
  var Ctor = value && value.constructor,
      proto = (typeof Ctor == 'function' && Ctor.prototype) || objectProto$8;

  return value === proto;
}

var _isPrototype = isPrototype;

/**
 * Creates a unary function that invokes `func` with its argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */
function overArg(func, transform) {
  return function(arg) {
    return func(transform(arg));
  };
}

var _overArg = overArg;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeKeys = _overArg(Object.keys, Object);

var _nativeKeys = nativeKeys;

/** Used for built-in method references. */
var objectProto$9 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$6 = objectProto$9.hasOwnProperty;

/**
 * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeys(object) {
  if (!_isPrototype(object)) {
    return _nativeKeys(object);
  }
  var result = [];
  for (var key in Object(object)) {
    if (hasOwnProperty$6.call(object, key) && key != 'constructor') {
      result.push(key);
    }
  }
  return result;
}

var _baseKeys = baseKeys;

/**
 * Checks if `value` is array-like. A value is considered array-like if it's
 * not a function and has a `value.length` that's an integer greater than or
 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
 * @example
 *
 * _.isArrayLike([1, 2, 3]);
 * // => true
 *
 * _.isArrayLike(document.body.children);
 * // => true
 *
 * _.isArrayLike('abc');
 * // => true
 *
 * _.isArrayLike(_.noop);
 * // => false
 */
function isArrayLike(value) {
  return value != null && isLength_1(value.length) && !isFunction_1(value);
}

var isArrayLike_1 = isArrayLike;

/**
 * Creates an array of the own enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects. See the
 * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * for more details.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keys(new Foo);
 * // => ['a', 'b'] (iteration order is not guaranteed)
 *
 * _.keys('hi');
 * // => ['0', '1']
 */
function keys(object) {
  return isArrayLike_1(object) ? _arrayLikeKeys(object) : _baseKeys(object);
}

var keys_1 = keys;

/**
 * Creates an array of own enumerable property names and symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */
function getAllKeys(object) {
  return _baseGetAllKeys(object, keys_1, _getSymbols);
}

var _getAllKeys = getAllKeys;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$2 = 1;

/** Used for built-in method references. */
var objectProto$a = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$7 = objectProto$a.hasOwnProperty;

/**
 * A specialized version of `baseIsEqualDeep` for objects with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function equalObjects(object, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG$2,
      objProps = _getAllKeys(object),
      objLength = objProps.length,
      othProps = _getAllKeys(other),
      othLength = othProps.length;

  if (objLength != othLength && !isPartial) {
    return false;
  }
  var index = objLength;
  while (index--) {
    var key = objProps[index];
    if (!(isPartial ? key in other : hasOwnProperty$7.call(other, key))) {
      return false;
    }
  }
  // Check that cyclic values are equal.
  var objStacked = stack.get(object);
  var othStacked = stack.get(other);
  if (objStacked && othStacked) {
    return objStacked == other && othStacked == object;
  }
  var result = true;
  stack.set(object, other);
  stack.set(other, object);

  var skipCtor = isPartial;
  while (++index < objLength) {
    key = objProps[index];
    var objValue = object[key],
        othValue = other[key];

    if (customizer) {
      var compared = isPartial
        ? customizer(othValue, objValue, key, other, object, stack)
        : customizer(objValue, othValue, key, object, other, stack);
    }
    // Recursively compare objects (susceptible to call stack limits).
    if (!(compared === undefined
          ? (objValue === othValue || equalFunc(objValue, othValue, bitmask, customizer, stack))
          : compared
        )) {
      result = false;
      break;
    }
    skipCtor || (skipCtor = key == 'constructor');
  }
  if (result && !skipCtor) {
    var objCtor = object.constructor,
        othCtor = other.constructor;

    // Non `Object` object instances with different constructors are not equal.
    if (objCtor != othCtor &&
        ('constructor' in object && 'constructor' in other) &&
        !(typeof objCtor == 'function' && objCtor instanceof objCtor &&
          typeof othCtor == 'function' && othCtor instanceof othCtor)) {
      result = false;
    }
  }
  stack['delete'](object);
  stack['delete'](other);
  return result;
}

var _equalObjects = equalObjects;

/* Built-in method references that are verified to be native. */
var DataView = _getNative(_root, 'DataView');

var _DataView = DataView;

/* Built-in method references that are verified to be native. */
var Promise = _getNative(_root, 'Promise');

var _Promise = Promise;

/* Built-in method references that are verified to be native. */
var Set = _getNative(_root, 'Set');

var _Set = Set;

/* Built-in method references that are verified to be native. */
var WeakMap = _getNative(_root, 'WeakMap');

var _WeakMap = WeakMap;

/** `Object#toString` result references. */
var mapTag$2 = '[object Map]',
    objectTag$1 = '[object Object]',
    promiseTag = '[object Promise]',
    setTag$2 = '[object Set]',
    weakMapTag$1 = '[object WeakMap]';

var dataViewTag$2 = '[object DataView]';

/** Used to detect maps, sets, and weakmaps. */
var dataViewCtorString = _toSource(_DataView),
    mapCtorString = _toSource(_Map),
    promiseCtorString = _toSource(_Promise),
    setCtorString = _toSource(_Set),
    weakMapCtorString = _toSource(_WeakMap);

/**
 * Gets the `toStringTag` of `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
var getTag = _baseGetTag;

// Fallback for data views, maps, sets, and weak maps in IE 11 and promises in Node.js < 6.
if ((_DataView && getTag(new _DataView(new ArrayBuffer(1))) != dataViewTag$2) ||
    (_Map && getTag(new _Map) != mapTag$2) ||
    (_Promise && getTag(_Promise.resolve()) != promiseTag) ||
    (_Set && getTag(new _Set) != setTag$2) ||
    (_WeakMap && getTag(new _WeakMap) != weakMapTag$1)) {
  getTag = function(value) {
    var result = _baseGetTag(value),
        Ctor = result == objectTag$1 ? value.constructor : undefined,
        ctorString = Ctor ? _toSource(Ctor) : '';

    if (ctorString) {
      switch (ctorString) {
        case dataViewCtorString: return dataViewTag$2;
        case mapCtorString: return mapTag$2;
        case promiseCtorString: return promiseTag;
        case setCtorString: return setTag$2;
        case weakMapCtorString: return weakMapTag$1;
      }
    }
    return result;
  };
}

var _getTag = getTag;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$3 = 1;

/** `Object#toString` result references. */
var argsTag$2 = '[object Arguments]',
    arrayTag$1 = '[object Array]',
    objectTag$2 = '[object Object]';

/** Used for built-in method references. */
var objectProto$b = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$8 = objectProto$b.hasOwnProperty;

/**
 * A specialized version of `baseIsEqual` for arrays and objects which performs
 * deep comparisons and tracks traversed objects enabling objects with circular
 * references to be compared.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} [stack] Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function baseIsEqualDeep(object, other, bitmask, customizer, equalFunc, stack) {
  var objIsArr = isArray_1(object),
      othIsArr = isArray_1(other),
      objTag = objIsArr ? arrayTag$1 : _getTag(object),
      othTag = othIsArr ? arrayTag$1 : _getTag(other);

  objTag = objTag == argsTag$2 ? objectTag$2 : objTag;
  othTag = othTag == argsTag$2 ? objectTag$2 : othTag;

  var objIsObj = objTag == objectTag$2,
      othIsObj = othTag == objectTag$2,
      isSameTag = objTag == othTag;

  if (isSameTag && isBuffer_1(object)) {
    if (!isBuffer_1(other)) {
      return false;
    }
    objIsArr = true;
    objIsObj = false;
  }
  if (isSameTag && !objIsObj) {
    stack || (stack = new _Stack);
    return (objIsArr || isTypedArray_1(object))
      ? _equalArrays(object, other, bitmask, customizer, equalFunc, stack)
      : _equalByTag(object, other, objTag, bitmask, customizer, equalFunc, stack);
  }
  if (!(bitmask & COMPARE_PARTIAL_FLAG$3)) {
    var objIsWrapped = objIsObj && hasOwnProperty$8.call(object, '__wrapped__'),
        othIsWrapped = othIsObj && hasOwnProperty$8.call(other, '__wrapped__');

    if (objIsWrapped || othIsWrapped) {
      var objUnwrapped = objIsWrapped ? object.value() : object,
          othUnwrapped = othIsWrapped ? other.value() : other;

      stack || (stack = new _Stack);
      return equalFunc(objUnwrapped, othUnwrapped, bitmask, customizer, stack);
    }
  }
  if (!isSameTag) {
    return false;
  }
  stack || (stack = new _Stack);
  return _equalObjects(object, other, bitmask, customizer, equalFunc, stack);
}

var _baseIsEqualDeep = baseIsEqualDeep;

/**
 * The base implementation of `_.isEqual` which supports partial comparisons
 * and tracks traversed objects.
 *
 * @private
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @param {boolean} bitmask The bitmask flags.
 *  1 - Unordered comparison
 *  2 - Partial comparison
 * @param {Function} [customizer] The function to customize comparisons.
 * @param {Object} [stack] Tracks traversed `value` and `other` objects.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 */
function baseIsEqual(value, other, bitmask, customizer, stack) {
  if (value === other) {
    return true;
  }
  if (value == null || other == null || (!isObjectLike_1(value) && !isObjectLike_1(other))) {
    return value !== value && other !== other;
  }
  return _baseIsEqualDeep(value, other, bitmask, customizer, baseIsEqual, stack);
}

var _baseIsEqual = baseIsEqual;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$4 = 1,
    COMPARE_UNORDERED_FLAG$2 = 2;

/**
 * The base implementation of `_.isMatch` without support for iteratee shorthands.
 *
 * @private
 * @param {Object} object The object to inspect.
 * @param {Object} source The object of property values to match.
 * @param {Array} matchData The property names, values, and compare flags to match.
 * @param {Function} [customizer] The function to customize comparisons.
 * @returns {boolean} Returns `true` if `object` is a match, else `false`.
 */
function baseIsMatch(object, source, matchData, customizer) {
  var index = matchData.length,
      length = index,
      noCustomizer = !customizer;

  if (object == null) {
    return !length;
  }
  object = Object(object);
  while (index--) {
    var data = matchData[index];
    if ((noCustomizer && data[2])
          ? data[1] !== object[data[0]]
          : !(data[0] in object)
        ) {
      return false;
    }
  }
  while (++index < length) {
    data = matchData[index];
    var key = data[0],
        objValue = object[key],
        srcValue = data[1];

    if (noCustomizer && data[2]) {
      if (objValue === undefined && !(key in object)) {
        return false;
      }
    } else {
      var stack = new _Stack;
      if (customizer) {
        var result = customizer(objValue, srcValue, key, object, source, stack);
      }
      if (!(result === undefined
            ? _baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG$4 | COMPARE_UNORDERED_FLAG$2, customizer, stack)
            : result
          )) {
        return false;
      }
    }
  }
  return true;
}

var _baseIsMatch = baseIsMatch;

/**
 * Checks if `value` is suitable for strict equality comparisons, i.e. `===`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` if suitable for strict
 *  equality comparisons, else `false`.
 */
function isStrictComparable(value) {
  return value === value && !isObject_1(value);
}

var _isStrictComparable = isStrictComparable;

/**
 * Gets the property names, values, and compare flags of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the match data of `object`.
 */
function getMatchData(object) {
  var result = keys_1(object),
      length = result.length;

  while (length--) {
    var key = result[length],
        value = object[key];

    result[length] = [key, value, _isStrictComparable(value)];
  }
  return result;
}

var _getMatchData = getMatchData;

/**
 * A specialized version of `matchesProperty` for source values suitable
 * for strict equality comparisons, i.e. `===`.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @param {*} srcValue The value to match.
 * @returns {Function} Returns the new spec function.
 */
function matchesStrictComparable(key, srcValue) {
  return function(object) {
    if (object == null) {
      return false;
    }
    return object[key] === srcValue &&
      (srcValue !== undefined || (key in Object(object)));
  };
}

var _matchesStrictComparable = matchesStrictComparable;

/**
 * The base implementation of `_.matches` which doesn't clone `source`.
 *
 * @private
 * @param {Object} source The object of property values to match.
 * @returns {Function} Returns the new spec function.
 */
function baseMatches(source) {
  var matchData = _getMatchData(source);
  if (matchData.length == 1 && matchData[0][2]) {
    return _matchesStrictComparable(matchData[0][0], matchData[0][1]);
  }
  return function(object) {
    return object === source || _baseIsMatch(object, source, matchData);
  };
}

var _baseMatches = baseMatches;

/**
 * Gets the value at `path` of `object`. If the resolved value is
 * `undefined`, the `defaultValue` is returned in its place.
 *
 * @static
 * @memberOf _
 * @since 3.7.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @param {*} [defaultValue] The value returned for `undefined` resolved values.
 * @returns {*} Returns the resolved value.
 * @example
 *
 * var object = { 'a': [{ 'b': { 'c': 3 } }] };
 *
 * _.get(object, 'a[0].b.c');
 * // => 3
 *
 * _.get(object, ['a', '0', 'b', 'c']);
 * // => 3
 *
 * _.get(object, 'a.b.c', 'default');
 * // => 'default'
 */
function get(object, path, defaultValue) {
  var result = object == null ? undefined : _baseGet(object, path);
  return result === undefined ? defaultValue : result;
}

var get_1 = get;

/**
 * The base implementation of `_.hasIn` without support for deep paths.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {Array|string} key The key to check.
 * @returns {boolean} Returns `true` if `key` exists, else `false`.
 */
function baseHasIn(object, key) {
  return object != null && key in Object(object);
}

var _baseHasIn = baseHasIn;

/**
 * Checks if `path` exists on `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path to check.
 * @param {Function} hasFunc The function to check properties.
 * @returns {boolean} Returns `true` if `path` exists, else `false`.
 */
function hasPath(object, path, hasFunc) {
  path = _castPath(path, object);

  var index = -1,
      length = path.length,
      result = false;

  while (++index < length) {
    var key = _toKey(path[index]);
    if (!(result = object != null && hasFunc(object, key))) {
      break;
    }
    object = object[key];
  }
  if (result || ++index != length) {
    return result;
  }
  length = object == null ? 0 : object.length;
  return !!length && isLength_1(length) && _isIndex(key, length) &&
    (isArray_1(object) || isArguments_1(object));
}

var _hasPath = hasPath;

/**
 * Checks if `path` is a direct or inherited property of `object`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path to check.
 * @returns {boolean} Returns `true` if `path` exists, else `false`.
 * @example
 *
 * var object = _.create({ 'a': _.create({ 'b': 2 }) });
 *
 * _.hasIn(object, 'a');
 * // => true
 *
 * _.hasIn(object, 'a.b');
 * // => true
 *
 * _.hasIn(object, ['a', 'b']);
 * // => true
 *
 * _.hasIn(object, 'b');
 * // => false
 */
function hasIn(object, path) {
  return object != null && _hasPath(object, path, _baseHasIn);
}

var hasIn_1 = hasIn;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$5 = 1,
    COMPARE_UNORDERED_FLAG$3 = 2;

/**
 * The base implementation of `_.matchesProperty` which doesn't clone `srcValue`.
 *
 * @private
 * @param {string} path The path of the property to get.
 * @param {*} srcValue The value to match.
 * @returns {Function} Returns the new spec function.
 */
function baseMatchesProperty(path, srcValue) {
  if (_isKey(path) && _isStrictComparable(srcValue)) {
    return _matchesStrictComparable(_toKey(path), srcValue);
  }
  return function(object) {
    var objValue = get_1(object, path);
    return (objValue === undefined && objValue === srcValue)
      ? hasIn_1(object, path)
      : _baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG$5 | COMPARE_UNORDERED_FLAG$3);
  };
}

var _baseMatchesProperty = baseMatchesProperty;

/**
 * This method returns the first argument it receives.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Util
 * @param {*} value Any value.
 * @returns {*} Returns `value`.
 * @example
 *
 * var object = { 'a': 1 };
 *
 * console.log(_.identity(object) === object);
 * // => true
 */
function identity(value) {
  return value;
}

var identity_1 = identity;

/**
 * The base implementation of `_.property` without support for deep paths.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @returns {Function} Returns the new accessor function.
 */
function baseProperty(key) {
  return function(object) {
    return object == null ? undefined : object[key];
  };
}

var _baseProperty = baseProperty;

/**
 * A specialized version of `baseProperty` which supports deep paths.
 *
 * @private
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new accessor function.
 */
function basePropertyDeep(path) {
  return function(object) {
    return _baseGet(object, path);
  };
}

var _basePropertyDeep = basePropertyDeep;

/**
 * Creates a function that returns the value at `path` of a given object.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new accessor function.
 * @example
 *
 * var objects = [
 *   { 'a': { 'b': 2 } },
 *   { 'a': { 'b': 1 } }
 * ];
 *
 * _.map(objects, _.property('a.b'));
 * // => [2, 1]
 *
 * _.map(_.sortBy(objects, _.property(['a', 'b'])), 'a.b');
 * // => [1, 2]
 */
function property(path) {
  return _isKey(path) ? _baseProperty(_toKey(path)) : _basePropertyDeep(path);
}

var property_1 = property;

/**
 * The base implementation of `_.iteratee`.
 *
 * @private
 * @param {*} [value=_.identity] The value to convert to an iteratee.
 * @returns {Function} Returns the iteratee.
 */
function baseIteratee(value) {
  // Don't store the `typeof` result in a variable to avoid a JIT bug in Safari 9.
  // See https://bugs.webkit.org/show_bug.cgi?id=156034 for more details.
  if (typeof value == 'function') {
    return value;
  }
  if (value == null) {
    return identity_1;
  }
  if (typeof value == 'object') {
    return isArray_1(value)
      ? _baseMatchesProperty(value[0], value[1])
      : _baseMatches(value);
  }
  return property_1(value);
}

var _baseIteratee = baseIteratee;

/**
 * Creates a base function for methods like `_.forIn` and `_.forOwn`.
 *
 * @private
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new base function.
 */
function createBaseFor(fromRight) {
  return function(object, iteratee, keysFunc) {
    var index = -1,
        iterable = Object(object),
        props = keysFunc(object),
        length = props.length;

    while (length--) {
      var key = props[fromRight ? length : ++index];
      if (iteratee(iterable[key], key, iterable) === false) {
        break;
      }
    }
    return object;
  };
}

var _createBaseFor = createBaseFor;

/**
 * The base implementation of `baseForOwn` which iterates over `object`
 * properties returned by `keysFunc` and invokes `iteratee` for each property.
 * Iteratee functions may exit iteration early by explicitly returning `false`.
 *
 * @private
 * @param {Object} object The object to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @returns {Object} Returns `object`.
 */
var baseFor = _createBaseFor();

var _baseFor = baseFor;

/**
 * The base implementation of `_.forOwn` without support for iteratee shorthands.
 *
 * @private
 * @param {Object} object The object to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Object} Returns `object`.
 */
function baseForOwn(object, iteratee) {
  return object && _baseFor(object, iteratee, keys_1);
}

var _baseForOwn = baseForOwn;

/**
 * Creates a `baseEach` or `baseEachRight` function.
 *
 * @private
 * @param {Function} eachFunc The function to iterate over a collection.
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new base function.
 */
function createBaseEach(eachFunc, fromRight) {
  return function(collection, iteratee) {
    if (collection == null) {
      return collection;
    }
    if (!isArrayLike_1(collection)) {
      return eachFunc(collection, iteratee);
    }
    var length = collection.length,
        index = fromRight ? length : -1,
        iterable = Object(collection);

    while ((fromRight ? index-- : ++index < length)) {
      if (iteratee(iterable[index], index, iterable) === false) {
        break;
      }
    }
    return collection;
  };
}

var _createBaseEach = createBaseEach;

/**
 * The base implementation of `_.forEach` without support for iteratee shorthands.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array|Object} Returns `collection`.
 */
var baseEach = _createBaseEach(_baseForOwn);

var _baseEach = baseEach;

/**
 * The base implementation of `_.map` without support for iteratee shorthands.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */
function baseMap(collection, iteratee) {
  var index = -1,
      result = isArrayLike_1(collection) ? Array(collection.length) : [];

  _baseEach(collection, function(value, key, collection) {
    result[++index] = iteratee(value, key, collection);
  });
  return result;
}

var _baseMap = baseMap;

/**
 * The base implementation of `_.sortBy` which uses `comparer` to define the
 * sort order of `array` and replaces criteria objects with their corresponding
 * values.
 *
 * @private
 * @param {Array} array The array to sort.
 * @param {Function} comparer The function to define sort order.
 * @returns {Array} Returns `array`.
 */
function baseSortBy(array, comparer) {
  var length = array.length;

  array.sort(comparer);
  while (length--) {
    array[length] = array[length].value;
  }
  return array;
}

var _baseSortBy = baseSortBy;

/**
 * Compares values to sort them in ascending order.
 *
 * @private
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {number} Returns the sort order indicator for `value`.
 */
function compareAscending(value, other) {
  if (value !== other) {
    var valIsDefined = value !== undefined,
        valIsNull = value === null,
        valIsReflexive = value === value,
        valIsSymbol = isSymbol_1(value);

    var othIsDefined = other !== undefined,
        othIsNull = other === null,
        othIsReflexive = other === other,
        othIsSymbol = isSymbol_1(other);

    if ((!othIsNull && !othIsSymbol && !valIsSymbol && value > other) ||
        (valIsSymbol && othIsDefined && othIsReflexive && !othIsNull && !othIsSymbol) ||
        (valIsNull && othIsDefined && othIsReflexive) ||
        (!valIsDefined && othIsReflexive) ||
        !valIsReflexive) {
      return 1;
    }
    if ((!valIsNull && !valIsSymbol && !othIsSymbol && value < other) ||
        (othIsSymbol && valIsDefined && valIsReflexive && !valIsNull && !valIsSymbol) ||
        (othIsNull && valIsDefined && valIsReflexive) ||
        (!othIsDefined && valIsReflexive) ||
        !othIsReflexive) {
      return -1;
    }
  }
  return 0;
}

var _compareAscending = compareAscending;

/**
 * Used by `_.orderBy` to compare multiple properties of a value to another
 * and stable sort them.
 *
 * If `orders` is unspecified, all values are sorted in ascending order. Otherwise,
 * specify an order of "desc" for descending or "asc" for ascending sort order
 * of corresponding values.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {boolean[]|string[]} orders The order to sort by for each property.
 * @returns {number} Returns the sort order indicator for `object`.
 */
function compareMultiple(object, other, orders) {
  var index = -1,
      objCriteria = object.criteria,
      othCriteria = other.criteria,
      length = objCriteria.length,
      ordersLength = orders.length;

  while (++index < length) {
    var result = _compareAscending(objCriteria[index], othCriteria[index]);
    if (result) {
      if (index >= ordersLength) {
        return result;
      }
      var order = orders[index];
      return result * (order == 'desc' ? -1 : 1);
    }
  }
  // Fixes an `Array#sort` bug in the JS engine embedded in Adobe applications
  // that causes it, under certain circumstances, to provide the same value for
  // `object` and `other`. See https://github.com/jashkenas/underscore/pull/1247
  // for more details.
  //
  // This also ensures a stable sort in V8 and other engines.
  // See https://bugs.chromium.org/p/v8/issues/detail?id=90 for more details.
  return object.index - other.index;
}

var _compareMultiple = compareMultiple;

/**
 * The base implementation of `_.orderBy` without param guards.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function[]|Object[]|string[]} iteratees The iteratees to sort by.
 * @param {string[]} orders The sort orders of `iteratees`.
 * @returns {Array} Returns the new sorted array.
 */
function baseOrderBy(collection, iteratees, orders) {
  if (iteratees.length) {
    iteratees = _arrayMap(iteratees, function(iteratee) {
      if (isArray_1(iteratee)) {
        return function(value) {
          return _baseGet(value, iteratee.length === 1 ? iteratee[0] : iteratee);
        }
      }
      return iteratee;
    });
  } else {
    iteratees = [identity_1];
  }

  var index = -1;
  iteratees = _arrayMap(iteratees, _baseUnary(_baseIteratee));

  var result = _baseMap(collection, function(value, key, collection) {
    var criteria = _arrayMap(iteratees, function(iteratee) {
      return iteratee(value);
    });
    return { 'criteria': criteria, 'index': ++index, 'value': value };
  });

  return _baseSortBy(result, function(object, other) {
    return _compareMultiple(object, other, orders);
  });
}

var _baseOrderBy = baseOrderBy;

/**
 * A faster alternative to `Function#apply`, this function invokes `func`
 * with the `this` binding of `thisArg` and the arguments of `args`.
 *
 * @private
 * @param {Function} func The function to invoke.
 * @param {*} thisArg The `this` binding of `func`.
 * @param {Array} args The arguments to invoke `func` with.
 * @returns {*} Returns the result of `func`.
 */
function apply(func, thisArg, args) {
  switch (args.length) {
    case 0: return func.call(thisArg);
    case 1: return func.call(thisArg, args[0]);
    case 2: return func.call(thisArg, args[0], args[1]);
    case 3: return func.call(thisArg, args[0], args[1], args[2]);
  }
  return func.apply(thisArg, args);
}

var _apply = apply;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax = Math.max;

/**
 * A specialized version of `baseRest` which transforms the rest array.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @param {Function} transform The rest array transform.
 * @returns {Function} Returns the new function.
 */
function overRest(func, start, transform) {
  start = nativeMax(start === undefined ? (func.length - 1) : start, 0);
  return function() {
    var args = arguments,
        index = -1,
        length = nativeMax(args.length - start, 0),
        array = Array(length);

    while (++index < length) {
      array[index] = args[start + index];
    }
    index = -1;
    var otherArgs = Array(start + 1);
    while (++index < start) {
      otherArgs[index] = args[index];
    }
    otherArgs[start] = transform(array);
    return _apply(func, this, otherArgs);
  };
}

var _overRest = overRest;

/**
 * Creates a function that returns `value`.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {*} value The value to return from the new function.
 * @returns {Function} Returns the new constant function.
 * @example
 *
 * var objects = _.times(2, _.constant({ 'a': 1 }));
 *
 * console.log(objects);
 * // => [{ 'a': 1 }, { 'a': 1 }]
 *
 * console.log(objects[0] === objects[1]);
 * // => true
 */
function constant(value) {
  return function() {
    return value;
  };
}

var constant_1 = constant;

var defineProperty = (function() {
  try {
    var func = _getNative(Object, 'defineProperty');
    func({}, '', {});
    return func;
  } catch (e) {}
}());

var _defineProperty = defineProperty;

/**
 * The base implementation of `setToString` without support for hot loop shorting.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var baseSetToString = !_defineProperty ? identity_1 : function(func, string) {
  return _defineProperty(func, 'toString', {
    'configurable': true,
    'enumerable': false,
    'value': constant_1(string),
    'writable': true
  });
};

var _baseSetToString = baseSetToString;

/** Used to detect hot functions by number of calls within a span of milliseconds. */
var HOT_COUNT = 800,
    HOT_SPAN = 16;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeNow = Date.now;

/**
 * Creates a function that'll short out and invoke `identity` instead
 * of `func` when it's called `HOT_COUNT` or more times in `HOT_SPAN`
 * milliseconds.
 *
 * @private
 * @param {Function} func The function to restrict.
 * @returns {Function} Returns the new shortable function.
 */
function shortOut(func) {
  var count = 0,
      lastCalled = 0;

  return function() {
    var stamp = nativeNow(),
        remaining = HOT_SPAN - (stamp - lastCalled);

    lastCalled = stamp;
    if (remaining > 0) {
      if (++count >= HOT_COUNT) {
        return arguments[0];
      }
    } else {
      count = 0;
    }
    return func.apply(undefined, arguments);
  };
}

var _shortOut = shortOut;

/**
 * Sets the `toString` method of `func` to return `string`.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var setToString = _shortOut(_baseSetToString);

var _setToString = setToString;

/**
 * The base implementation of `_.rest` which doesn't validate or coerce arguments.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @returns {Function} Returns the new function.
 */
function baseRest(func, start) {
  return _setToString(_overRest(func, start, identity_1), func + '');
}

var _baseRest = baseRest;

/**
 * Checks if the given arguments are from an iteratee call.
 *
 * @private
 * @param {*} value The potential iteratee value argument.
 * @param {*} index The potential iteratee index or key argument.
 * @param {*} object The potential iteratee object argument.
 * @returns {boolean} Returns `true` if the arguments are from an iteratee call,
 *  else `false`.
 */
function isIterateeCall(value, index, object) {
  if (!isObject_1(object)) {
    return false;
  }
  var type = typeof index;
  if (type == 'number'
        ? (isArrayLike_1(object) && _isIndex(index, object.length))
        : (type == 'string' && index in object)
      ) {
    return eq_1(object[index], value);
  }
  return false;
}

var _isIterateeCall = isIterateeCall;

/**
 * Creates an array of elements, sorted in ascending order by the results of
 * running each element in a collection thru each iteratee. This method
 * performs a stable sort, that is, it preserves the original sort order of
 * equal elements. The iteratees are invoked with one argument: (value).
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Collection
 * @param {Array|Object} collection The collection to iterate over.
 * @param {...(Function|Function[])} [iteratees=[_.identity]]
 *  The iteratees to sort by.
 * @returns {Array} Returns the new sorted array.
 * @example
 *
 * var users = [
 *   { 'user': 'fred',   'age': 48 },
 *   { 'user': 'barney', 'age': 36 },
 *   { 'user': 'fred',   'age': 30 },
 *   { 'user': 'barney', 'age': 34 }
 * ];
 *
 * _.sortBy(users, [function(o) { return o.user; }]);
 * // => objects for [['barney', 36], ['barney', 34], ['fred', 48], ['fred', 30]]
 *
 * _.sortBy(users, ['user', 'age']);
 * // => objects for [['barney', 34], ['barney', 36], ['fred', 30], ['fred', 48]]
 */
var sortBy = _baseRest(function(collection, iteratees) {
  if (collection == null) {
    return [];
  }
  var length = iteratees.length;
  if (length > 1 && _isIterateeCall(collection, iteratees[0], iteratees[1])) {
    iteratees = [];
  } else if (length > 2 && _isIterateeCall(iteratees[0], iteratees[1], iteratees[2])) {
    iteratees = [iteratees[0]];
  }
  return _baseOrderBy(collection, _baseFlatten(iteratees, 1), []);
});

var ScoreGroupsEnum;
(function (ScoreGroupsEnum) {
    ScoreGroupsEnum["HOME"] = "0";
    ScoreGroupsEnum["DRAW"] = "1";
    ScoreGroupsEnum["AWAY"] = "2";
})(ScoreGroupsEnum || (ScoreGroupsEnum = {}));

const getHomeScore = (probability) => {
    var _a, _b;
    return +((_b = (_a = probability.parameters.find(p => [ParameterType.HomeScore, ParameterType.HalfHomeScore].includes(p.type))) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : 0);
};
const getAwayScore = (probability) => {
    var _a, _b;
    return +((_b = (_a = probability.parameters.find(p => [ParameterType.AwayScore, ParameterType.HalfAwayScore].includes(p.type))) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : 0);
};
const groupByScore = (probability) => {
    const homeScore = getHomeScore(probability);
    const awayScore = getAwayScore(probability);
    if (homeScore > awayScore) {
        return ScoreGroupsEnum.HOME;
    }
    if (homeScore < awayScore) {
        return ScoreGroupsEnum.AWAY;
    }
    return ScoreGroupsEnum.DRAW;
};
const sortByGroup = (group, sort = OrderDirection.Asc) => (a, b) => {
    const aHomeScore = getHomeScore(a);
    const bHomeScore = getHomeScore(b);
    const aAwayScore = getAwayScore(a);
    const bAwayScore = getAwayScore(b);
    switch (group) {
        case ScoreGroupsEnum.HOME:
        case ScoreGroupsEnum.DRAW: {
            if (aHomeScore !== bHomeScore) {
                return sort === OrderDirection.Asc
                    ? aHomeScore - bHomeScore
                    : bHomeScore - aHomeScore;
            }
            else {
                return sort === OrderDirection.Asc
                    ? aAwayScore - bAwayScore
                    : bAwayScore - aAwayScore;
            }
        }
        case ScoreGroupsEnum.AWAY:
        default:
            if (aAwayScore !== bAwayScore) {
                return sort === OrderDirection.Asc
                    ? aAwayScore - bAwayScore
                    : bAwayScore - aAwayScore;
            }
            else {
                return sort === OrderDirection.Asc
                    ? aHomeScore - bHomeScore
                    : bHomeScore - aHomeScore;
            }
    }
};
const isAnyFn = (probability) => (parameter) => probability.parameters.find(p => p.type === parameter && p.value === '-1');
const extractAnyOtherOutcome = (probabilities) => {
    const anyOtherOutcome = probabilities.find(probability => [ParameterType.HomeScore, ParameterType.AwayScore].every(isAnyFn(probability)));
    return {
        anyOtherOutcome,
        probabilitiesWithoutAnyOther: probabilities.filter(probability => ![ParameterType.HomeScore, ParameterType.AwayScore].every(isAnyFn(probability))),
    };
};
const getOutcomeFullName = (renderName = p => p.outcomeTypeData.name, probability, categoryMarket) => `${renderName(probability)} ${categoryMarket.appendedParameterTypes
    .map(item => { var _a; return (_a = probability.parameters.find(p => p.type === item)) === null || _a === void 0 ? void 0 : _a.value; })
    .filter(Boolean)
    .join(', ')}`;

const K1Template = ({ probabilities, renderName, ...props }) => {
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => probabilities.map(p => [
        getOutcomeFullName(renderName, p, props.categoryMarket),
        p,
    ]), [props.categoryMarket, renderName]);
    return (React.createElement(OddsTableGroup, { columns: ['label', 'odd'], transformProbabilities: transformData, probabilities: filteredProbabilities, labelInButton: true, ...props }));
};

const OddName = {
    K2: {
        first: 'П1_x2',
        second: 'П2_x2',
    },
    K3: {
        first: 'П1',
        second: 'Х',
        third: 'П2',
    },
};

function listToMatrix(list, elementsPerSubArray) {
    const matrix = [];
    let i;
    let k;
    for (i = 0, k = -1; i < list.length; i += 1) {
        if (i % elementsPerSubArray === 0) {
            k += 1;
            matrix[k] = [];
        }
        matrix[k].push(list[i]);
    }
    return matrix;
}
function insertEmptyOrNothing(addEmpty, emptyValue = ['']) {
    return [...(addEmpty ? emptyValue : [])];
}
function sparseArrayK2(sortedProbabilities, flatRows) {
    const { K2 } = OddName;
    const { outcomeTypeData } = sortedProbabilities[0] || {};
    if ((outcomeTypeData === null || outcomeTypeData === void 0 ? void 0 : outcomeTypeData.code) === K2.second) {
        flatRows[0].unshift('', '');
    }
}
function sparseArrayK3(probabilities, flatRows) {
    var _a, _b;
    const firstOutcomeName = (_a = probabilities[0]) === null || _a === void 0 ? void 0 : _a.outcomeTypeData.code;
    const secondOutcomeName = (_b = probabilities[1]) === null || _b === void 0 ? void 0 : _b.outcomeTypeData.code;
    const isOneOutcome = probabilities.length === 1;
    const isTwoOutcomes = probabilities.length === 2;
    const { K3 } = OddName;
    if (isTwoOutcomes) {
        if (firstOutcomeName === K3.second) {
            flatRows[0].unshift('', '');
        }
        else if (firstOutcomeName === K3.first &&
            secondOutcomeName === K3.third) {
            flatRows[0].splice(2, 0, '', '');
        }
    }
    if (isOneOutcome) {
        if (firstOutcomeName === K3.second) {
            flatRows[0].unshift('', '');
        }
        else if (firstOutcomeName === K3.third) {
            flatRows[0].unshift('', '', '', '');
        }
    }
}

const K2Template = ({ probabilities, renderName, ...props }) => {
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        const sortedProbabilities = sortProbabilitiesByParamValue(probabilities);
        const matrix = listToMatrix(sortedProbabilities, 2);
        const flatRows = matrix
            .map(row => row.map(p => [
            getOutcomeFullName(renderName, p, props.categoryMarket),
            p,
        ]))
            .map(p => p.flat());
        if (sortedProbabilities.length === 1) {
            sparseArrayK2(sortedProbabilities, flatRows);
        }
        return flatRows;
    }, [props.categoryMarket, renderName]);
    return (React.createElement(OddsTableGroup, { columns: ['label', 'odd', 'label', 'odd'], transformProbabilities: transformData, probabilities: filteredProbabilities, labelInButton: true, ...props }));
};

const LK2Template = ({ probabilities, renderName, ...props }) => {
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        return listToMatrix(sortProbabilitiesByParamValue(probabilities), 2)
            .map(row => row.map(p => [
            getOutcomeFullName(renderName, p, props.categoryMarket),
            p,
        ]))
            .map(p => p.flat());
    }, [props.categoryMarket, renderName]);
    return (React.createElement(OddsTableGroup, { title: props.categoryMarket.title, columns: ['label', 'odd', 'label', 'odd'], transformProbabilities: transformData, probabilities: filteredProbabilities, labelInButton: true, ...props }));
};

/**
 * The base implementation of `assignValue` and `assignMergeValue` without
 * value checks.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function baseAssignValue(object, key, value) {
  if (key == '__proto__' && _defineProperty) {
    _defineProperty(object, key, {
      'configurable': true,
      'enumerable': true,
      'value': value,
      'writable': true
    });
  } else {
    object[key] = value;
  }
}

var _baseAssignValue = baseAssignValue;

/**
 * A specialized version of `baseAggregator` for arrays.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} setter The function to set `accumulator` values.
 * @param {Function} iteratee The iteratee to transform keys.
 * @param {Object} accumulator The initial aggregated object.
 * @returns {Function} Returns `accumulator`.
 */
function arrayAggregator(array, setter, iteratee, accumulator) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    var value = array[index];
    setter(accumulator, value, iteratee(value), array);
  }
  return accumulator;
}

var _arrayAggregator = arrayAggregator;

/**
 * Aggregates elements of `collection` on `accumulator` with keys transformed
 * by `iteratee` and values set by `setter`.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} setter The function to set `accumulator` values.
 * @param {Function} iteratee The iteratee to transform keys.
 * @param {Object} accumulator The initial aggregated object.
 * @returns {Function} Returns `accumulator`.
 */
function baseAggregator(collection, setter, iteratee, accumulator) {
  _baseEach(collection, function(value, key, collection) {
    setter(accumulator, value, iteratee(value), collection);
  });
  return accumulator;
}

var _baseAggregator = baseAggregator;

/**
 * Creates a function like `_.groupBy`.
 *
 * @private
 * @param {Function} setter The function to set accumulator values.
 * @param {Function} [initializer] The accumulator object initializer.
 * @returns {Function} Returns the new aggregator function.
 */
function createAggregator(setter, initializer) {
  return function(collection, iteratee) {
    var func = isArray_1(collection) ? _arrayAggregator : _baseAggregator,
        accumulator = initializer ? initializer() : {};

    return func(collection, setter, _baseIteratee(iteratee), accumulator);
  };
}

var _createAggregator = createAggregator;

/** Used for built-in method references. */
var objectProto$c = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$9 = objectProto$c.hasOwnProperty;

/**
 * Creates an object composed of keys generated from the results of running
 * each element of `collection` thru `iteratee`. The order of grouped values
 * is determined by the order they occur in `collection`. The corresponding
 * value of each key is an array of elements responsible for generating the
 * key. The iteratee is invoked with one argument: (value).
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Collection
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} [iteratee=_.identity] The iteratee to transform keys.
 * @returns {Object} Returns the composed aggregate object.
 * @example
 *
 * _.groupBy([6.1, 4.2, 6.3], Math.floor);
 * // => { '4': [4.2], '6': [6.1, 6.3] }
 *
 * // The `_.property` iteratee shorthand.
 * _.groupBy(['one', 'two', 'three'], 'length');
 * // => { '3': ['one', 'two'], '5': ['three'] }
 */
var groupBy = _createAggregator(function(result, value, key) {
  if (hasOwnProperty$9.call(result, key)) {
    result[key].push(value);
  } else {
    _baseAssignValue(result, key, [value]);
  }
});

var groupBy_1 = groupBy;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeCeil = Math.ceil,
    nativeMax$1 = Math.max;

/**
 * The base implementation of `_.range` and `_.rangeRight` which doesn't
 * coerce arguments.
 *
 * @private
 * @param {number} start The start of the range.
 * @param {number} end The end of the range.
 * @param {number} step The value to increment or decrement by.
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Array} Returns the range of numbers.
 */
function baseRange(start, end, step, fromRight) {
  var index = -1,
      length = nativeMax$1(nativeCeil((end - start) / (step || 1)), 0),
      result = Array(length);

  while (length--) {
    result[fromRight ? length : ++index] = start;
    start += step;
  }
  return result;
}

var _baseRange = baseRange;

/** Used to match a single whitespace character. */
var reWhitespace = /\s/;

/**
 * Used by `_.trim` and `_.trimEnd` to get the index of the last non-whitespace
 * character of `string`.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {number} Returns the index of the last non-whitespace character.
 */
function trimmedEndIndex(string) {
  var index = string.length;

  while (index-- && reWhitespace.test(string.charAt(index))) {}
  return index;
}

var _trimmedEndIndex = trimmedEndIndex;

/** Used to match leading whitespace. */
var reTrimStart = /^\s+/;

/**
 * The base implementation of `_.trim`.
 *
 * @private
 * @param {string} string The string to trim.
 * @returns {string} Returns the trimmed string.
 */
function baseTrim(string) {
  return string
    ? string.slice(0, _trimmedEndIndex(string) + 1).replace(reTrimStart, '')
    : string;
}

var _baseTrim = baseTrim;

/** Used as references for various `Number` constants. */
var NAN = 0 / 0;

/** Used to detect bad signed hexadecimal string values. */
var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

/** Used to detect binary string values. */
var reIsBinary = /^0b[01]+$/i;

/** Used to detect octal string values. */
var reIsOctal = /^0o[0-7]+$/i;

/** Built-in method references without a dependency on `root`. */
var freeParseInt = parseInt;

/**
 * Converts `value` to a number.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {number} Returns the number.
 * @example
 *
 * _.toNumber(3.2);
 * // => 3.2
 *
 * _.toNumber(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toNumber(Infinity);
 * // => Infinity
 *
 * _.toNumber('3.2');
 * // => 3.2
 */
function toNumber(value) {
  if (typeof value == 'number') {
    return value;
  }
  if (isSymbol_1(value)) {
    return NAN;
  }
  if (isObject_1(value)) {
    var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
    value = isObject_1(other) ? (other + '') : other;
  }
  if (typeof value != 'string') {
    return value === 0 ? value : +value;
  }
  value = _baseTrim(value);
  var isBinary = reIsBinary.test(value);
  return (isBinary || reIsOctal.test(value))
    ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
    : (reIsBadHex.test(value) ? NAN : +value);
}

var toNumber_1 = toNumber;

/** Used as references for various `Number` constants. */
var INFINITY$2 = 1 / 0,
    MAX_INTEGER = 1.7976931348623157e+308;

/**
 * Converts `value` to a finite number.
 *
 * @static
 * @memberOf _
 * @since 4.12.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {number} Returns the converted number.
 * @example
 *
 * _.toFinite(3.2);
 * // => 3.2
 *
 * _.toFinite(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toFinite(Infinity);
 * // => 1.7976931348623157e+308
 *
 * _.toFinite('3.2');
 * // => 3.2
 */
function toFinite(value) {
  if (!value) {
    return value === 0 ? value : 0;
  }
  value = toNumber_1(value);
  if (value === INFINITY$2 || value === -INFINITY$2) {
    var sign = (value < 0 ? -1 : 1);
    return sign * MAX_INTEGER;
  }
  return value === value ? value : 0;
}

var toFinite_1 = toFinite;

/**
 * Creates a `_.range` or `_.rangeRight` function.
 *
 * @private
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new range function.
 */
function createRange(fromRight) {
  return function(start, end, step) {
    if (step && typeof step != 'number' && _isIterateeCall(start, end, step)) {
      end = step = undefined;
    }
    // Ensure the sign of `-0` is preserved.
    start = toFinite_1(start);
    if (end === undefined) {
      end = start;
      start = 0;
    } else {
      end = toFinite_1(end);
    }
    step = step === undefined ? (start < end ? 1 : -1) : toFinite_1(step);
    return _baseRange(start, end, step, fromRight);
  };
}

var _createRange = createRange;

/**
 * Creates an array of numbers (positive and/or negative) progressing from
 * `start` up to, but not including, `end`. A step of `-1` is used if a negative
 * `start` is specified without an `end` or `step`. If `end` is not specified,
 * it's set to `start` with `start` then set to `0`.
 *
 * **Note:** JavaScript follows the IEEE-754 standard for resolving
 * floating-point values which can produce unexpected results.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Util
 * @param {number} [start=0] The start of the range.
 * @param {number} end The end of the range.
 * @param {number} [step=1] The value to increment or decrement by.
 * @returns {Array} Returns the range of numbers.
 * @see _.inRange, _.rangeRight
 * @example
 *
 * _.range(4);
 * // => [0, 1, 2, 3]
 *
 * _.range(-4);
 * // => [0, -1, -2, -3]
 *
 * _.range(1, 5);
 * // => [1, 2, 3, 4]
 *
 * _.range(0, 20, 5);
 * // => [0, 5, 10, 15]
 *
 * _.range(0, -4, -1);
 * // => [0, -1, -2, -3]
 *
 * _.range(1, 4, 0);
 * // => [1, 1, 1]
 *
 * _.range(0);
 * // => []
 */
var range = _createRange();

var range_1 = range;

/**
 * Creates an object with the same keys as `object` and values generated
 * by running each own enumerable string keyed property of `object` thru
 * `iteratee`. The iteratee is invoked with three arguments:
 * (value, key, object).
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Object
 * @param {Object} object The object to iterate over.
 * @param {Function} [iteratee=_.identity] The function invoked per iteration.
 * @returns {Object} Returns the new mapped object.
 * @see _.mapKeys
 * @example
 *
 * var users = {
 *   'fred':    { 'user': 'fred',    'age': 40 },
 *   'pebbles': { 'user': 'pebbles', 'age': 1 }
 * };
 *
 * _.mapValues(users, function(o) { return o.age; });
 * // => { 'fred': 40, 'pebbles': 1 } (iteration order is not guaranteed)
 *
 * // The `_.property` iteratee shorthand.
 * _.mapValues(users, 'age');
 * // => { 'fred': 40, 'pebbles': 1 } (iteration order is not guaranteed)
 */
function mapValues(object, iteratee) {
  var result = {};
  iteratee = _baseIteratee(iteratee);

  _baseForOwn(object, function(value, key, object) {
    _baseAssignValue(result, key, iteratee(value, key, object));
  });
  return result;
}

var mapValues_1 = mapValues;

const SK2Template = ({ probabilities, renderName, ...props }) => {
    const { t } = useTranslation();
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        var _a, _b, _c;
        const { anyOtherOutcome, probabilitiesWithoutAnyOther } = extractAnyOtherOutcome(probabilities);
        const rows = Object.values(mapValues_1(groupBy_1(probabilitiesWithoutAnyOther, groupByScore), (groupsProbabilities, group) => groupsProbabilities.sort(sortByGroup(group))));
        if (anyOtherOutcome) {
            const columnToPush = ((_a = rows[0]) === null || _a === void 0 ? void 0 : _a.length) <= ((_b = rows[1]) === null || _b === void 0 ? void 0 : _b.length) ? 0 : 1;
            const anyOtherOutcomeUpdated = {
                ...anyOtherOutcome,
                outcomeTypeData: {
                    ...anyOtherOutcome.outcomeTypeData,
                    name: t('anyOther')
                }
            };
            (_c = rows[columnToPush]) === null || _c === void 0 ? void 0 : _c.push(anyOtherOutcomeUpdated);
        }
        const maxLength = Math.max(...rows.map(group => group.length));
        return range_1(0, maxLength).map(rowNumber => rows
            .map(group => group[rowNumber])
            .map(probability => {
            if (probability) {
                return [
                    getOutcomeFullName(renderName, probability, props.categoryMarket),
                    probability,
                ];
            }
            else {
                return ['', ''];
            }
        })
            .flat());
    }, [props.categoryMarket, renderName, t]);
    return (React.createElement(OddsTableGroup, { columns: ['label', 'odd', 'label', 'odd'], transformProbabilities: transformData, title: props.categoryMarket.title, probabilities: filteredProbabilities, ...props }));
};

const invertAwayProbabilityValueParameter = (probability) => {
    const newProbability = { ...probability };
    if (probability.outcomeTypeData.marketLink === MarketLink.HandicapAway) {
        newProbability.parameters = newProbability.parameters.map(parameter => {
            if (parameter.type === ParameterType.Value) {
                const isInteger = parameter.value.split('.')[1] === '0';
                return {
                    type: parameter.type,
                    value: `${+parameter.value * -1}${isInteger ? '.0' : ''}`,
                };
            }
            else
                return parameter;
        });
    }
    return newProbability;
};
const H2Template = ({ probabilities, renderName, ...props }) => {
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        const invertHandicaps = probabilities.map(invertAwayProbabilityValueParameter);
        const groupsByValue = groupBy_1(invertHandicaps, probability => {
            var _a;
            return Number((_a = probability.parameters.find(p => p.type === ParameterType.Value)) === null || _a === void 0 ? void 0 : _a.value);
        });
        const rows = Object.keys(groupsByValue)
            .sort((a, b) => Number(a) - Number(b))
            .map(key => groupsByValue[key]
            .map(invertAwayProbabilityValueParameter)
            .sort(a => {
            if (a.outcomeTypeData.marketLink === MarketLink.TotalOver) {
                return -1;
            }
            return 0;
        }))
            .map(row => {
            let addSpacing = false;
            if (row.length === 1 &&
                [MarketLink.HandicapAway].includes(row[0].outcomeTypeData.marketLink)) {
                addSpacing = true;
            }
            return row.map(p => [
                ...insertEmptyOrNothing(addSpacing, ['', '']),
                getOutcomeFullName(renderName, p, props.categoryMarket),
                p,
            ]);
        })
            .map(p => p.flat());
        return rows;
    }, [props.categoryMarket, renderName]);
    return (React.createElement(OddsTableGroup, { transformProbabilities: transformData, title: [
            props.isMobile &&
                !props.isPlain && [
                ...[
                    props.competitors.homeCompetitors.map(a => a.name).join(','),
                    '',
                    props.competitors.awayCompetitors.map(a => a.name).join(','),
                    '',
                ],
            ],
        ], columns: ['label', 'odd', 'label', 'odd'], probabilities: filteredProbabilities, labelInButton: true, ...props }));
};

const invertAwayProbabilityValueParameter$1 = (probability) => {
    const newProbability = { ...probability };
    if (probability.outcomeTypeData.marketLink === MarketLink.HandicapAway) {
        newProbability.parameters = newProbability.parameters.map(parameter => {
            if (parameter.type === ParameterType.Value) {
                const isInteger = parameter.value.split('.')[1] === '0';
                return {
                    type: parameter.type,
                    value: `${+parameter.value * -1}${isInteger ? '.0' : ''}`,
                };
            }
            else
                return parameter;
        });
    }
    return newProbability;
};
const LH2Template = ({ probabilities, renderName, ...props }) => {
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        const invertHandicaps = probabilities.map(invertAwayProbabilityValueParameter$1);
        const groupsByValue = groupBy_1(invertHandicaps, probability => {
            var _a;
            return Number((_a = probability.parameters.find(p => p.type === ParameterType.Value)) === null || _a === void 0 ? void 0 : _a.value);
        });
        const rows = Object.keys(groupsByValue)
            .sort((a, b) => Number(a) - Number(b))
            .map(key => groupsByValue[key]
            .map(invertAwayProbabilityValueParameter$1)
            .sort(a => {
            if (a.outcomeTypeData.marketLink === MarketLink.TotalOver) {
                return -1;
            }
            return 0;
        }))
            .map(row => {
            let addSpacing = false;
            if (row.length === 1 &&
                [MarketLink.HandicapAway].includes(row[0].outcomeTypeData.marketLink)) {
                addSpacing = true;
            }
            return row.map(p => [
                ...insertEmptyOrNothing(addSpacing, ['', '']),
                getOutcomeFullName(renderName, p, props.categoryMarket),
                p,
            ]);
        })
            .map(p => p.flat());
        return rows;
    }, [props.categoryMarket, renderName]);
    return (React.createElement(OddsTableGroup, { transformProbabilities: transformData, title: [
            [props.categoryMarket.title],
            props.isMobile &&
                !props.isPlain && [
                ...[
                    props.competitors.homeCompetitors.map(a => a.name).join(','),
                    '',
                    props.competitors.awayCompetitors.map(a => a.name).join(','),
                    '',
                ],
            ],
        ], columns: ['label', 'odd', 'label', 'odd'], probabilities: filteredProbabilities, labelInButton: true, ...props }));
};

const isInMarketLink = (row, marketLinks) => row === null || row === void 0 ? void 0 : row.some(item => {
    if (!item)
        return false;
    if (typeof item !== 'object')
        return false;
    if (marketLinks.includes(item.outcomeTypeData.marketLink)) {
        return true;
    }
    return false;
});

const totalMarketLinks = [MarketLink.TotalOver, MarketLink.TotalUnder];
const totalTranslationKeys = ['over', 'under'];
const yesNoMarketLinks = [MarketLink.Yes, MarketLink.No];
const yesNoTranslationKeys = ['yes', 'no'];
const G2Template = ({ probabilities, renderName, ...props }) => {
    const { t } = useTranslation();
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        const byParameter = groupBy_1(probabilities, probability => {
            var _a;
            return Math.abs(+((_a = probability.parameters.find(p => p.type === ParameterType.Value)) === null || _a === void 0 ? void 0 : _a.value));
        });
        const data = Object.entries(byParameter)
            .sort(([keyA, valueA], [keyB, valueB]) => {
            const isATotal = isInMarketLink(valueA, totalMarketLinks);
            const isBTotal = isInMarketLink(valueB, totalMarketLinks);
            if (isATotal && isBTotal) {
                return Number(keyA) - Number(keyB);
            }
            return 0;
        })
            .map(([, value]) => {
            let addSpacing = false;
            if (value.length === 1 &&
                [MarketLink.No, MarketLink.TotalUnder].includes(value[0].outcomeTypeData.marketLink)) {
                addSpacing = true;
            }
            return [
                getOutcomeFullName(renderName, value[0], props.categoryMarket),
                ...insertEmptyOrNothing(addSpacing),
                ...value.sort(a => {
                    if (a.outcomeTypeData.marketLink === MarketLink.Yes) {
                        return -1;
                    }
                    if (a.outcomeTypeData.marketLink === MarketLink.TotalOver) {
                        return -1;
                    }
                    return 1;
                }),
            ];
        });
        return data;
    }, [props.categoryMarket, renderName]);
    const titles = useMemo(() => {
        const isTotal = isInMarketLink(filteredProbabilities, totalMarketLinks);
        const isYesNo = isInMarketLink(filteredProbabilities, yesNoMarketLinks);
        if (props.isPlain) {
            return [];
        }
        return (isTotal ? totalTranslationKeys : isYesNo ? yesNoTranslationKeys : []).map(item => t(item));
    }, [filteredProbabilities, props.isPlain, t]);
    return (React.createElement(OddsTableGroup, { transformProbabilities: transformData, title: ['', ...titles], columns: ['label', 'odd', 'odd'], probabilities: filteredProbabilities, ...props }));
};

const totalMarketLinks$1 = [MarketLink.TotalOver, MarketLink.TotalUnder];
const totalTranslationKeys$1 = ['over', 'under'];
const yesNoMarketLinks$1 = [MarketLink.Yes, MarketLink.No];
const yesNoTranslationKeys$1 = ['yes', 'no'];
const LG2Template = ({ probabilities, renderName, ...props }) => {
    const { t } = useTranslation();
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        const byParameter = groupBy_1(probabilities, probability => {
            var _a;
            return Math.abs(+((_a = probability.parameters.find(p => p.type === ParameterType.Value)) === null || _a === void 0 ? void 0 : _a.value));
        });
        const data = Object.entries(byParameter)
            .sort(([keyA, valueA], [keyB, valueB]) => {
            const isATotal = isInMarketLink(valueA, totalMarketLinks$1);
            const isBTotal = isInMarketLink(valueB, totalMarketLinks$1);
            if (isATotal && isBTotal) {
                return Number(keyA) - Number(keyB);
            }
            return 0;
        })
            .map(([, value]) => {
            let addSpacing = false;
            if (value.length === 1 &&
                [MarketLink.No, MarketLink.TotalUnder].includes(value[0].outcomeTypeData.marketLink)) {
                addSpacing = true;
            }
            return [
                getOutcomeFullName(renderName, value[0], props.categoryMarket),
                ...insertEmptyOrNothing(addSpacing),
                ...value.sort(a => {
                    if (a.outcomeTypeData.marketLink === MarketLink.Yes) {
                        return -1;
                    }
                    if (a.outcomeTypeData.marketLink === MarketLink.TotalOver) {
                        return -1;
                    }
                    return 1;
                }),
            ];
        });
        return data;
    }, [props.categoryMarket, renderName]);
    const titles = useMemo(() => {
        const isTotal = isInMarketLink(filteredProbabilities, totalMarketLinks$1);
        const isYesNo = isInMarketLink(filteredProbabilities, yesNoMarketLinks$1);
        if (props.isPlain) {
            return [];
        }
        return (isTotal ? totalTranslationKeys$1 : isYesNo ? yesNoTranslationKeys$1 : []).map(item => t(item));
    }, [filteredProbabilities, props.isPlain, t]);
    return (React.createElement(OddsTableGroup, { transformProbabilities: transformData, title: [props.categoryMarket.title, ...titles], columns: ['label', 'odd', 'odd'], probabilities: filteredProbabilities, ...props }));
};

const K3Template = ({ probabilities, renderName, ...props }) => {
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        const matrix = listToMatrix(probabilities, 3);
        const flatRows = matrix
            .map(row => row.map(p => [
            getOutcomeFullName(renderName, p, props.categoryMarket),
            p,
        ]))
            .map(p => p.flat());
        if (probabilities.length < 3) {
            sparseArrayK3(probabilities, flatRows);
        }
        return flatRows;
    }, [props.categoryMarket, renderName]);
    return (React.createElement(OddsTableGroup, { columns: ['label', 'odd', 'label', 'odd', 'label', 'odd'], transformProbabilities: transformData, probabilities: filteredProbabilities, labelInButton: true, ...props }));
};

const LK3Template = ({ probabilities, renderName, ...props }) => {
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        return listToMatrix(sortProbabilitiesByParamValue(probabilities), 3)
            .map(row => row.map(p => [
            getOutcomeFullName(renderName, p, props.categoryMarket),
            p,
        ]))
            .map(p => p.flat());
    }, [props.categoryMarket, renderName]);
    return (React.createElement(OddsTableGroup, { title: props.categoryMarket.title, columns: ['label', 'odd', 'label', 'odd', 'label', 'odd'], transformProbabilities: transformData, probabilities: filteredProbabilities, labelInButton: true, ...props }));
};

const SK3Template = ({ probabilities, renderName, ...props }) => {
    const { t } = useTranslation();
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        const { anyOtherOutcome, probabilitiesWithoutAnyOther } = extractAnyOtherOutcome(probabilities);
        const rows = Object.values({
            ...{
                0: [],
                1: [],
                2: [],
            },
            ...mapValues_1(groupBy_1(probabilitiesWithoutAnyOther, groupByScore), (groupsProbabilities, group) => groupsProbabilities.sort(sortByGroup(group))),
        });
        if (anyOtherOutcome) {
            const anyOtherOutcomeUpdated = {
                ...anyOtherOutcome,
                outcomeTypeData: {
                    ...anyOtherOutcome.outcomeTypeData,
                    name: t('anyOther')
                }
            };
            rows[1].push(anyOtherOutcomeUpdated);
        }
        const maxLength = Math.max(...rows.map(group => group.length));
        return range_1(0, maxLength).map(rowNumber => rows
            .map(group => group[rowNumber])
            .map(probability => {
            if (probability) {
                return [
                    getOutcomeFullName(renderName, probability, props.categoryMarket),
                    probability,
                ];
            }
            else {
                return ['', ''];
            }
        })
            .flat());
    }, [props.categoryMarket, renderName, t]);
    return (React.createElement(OddsTableGroup, { columns: ['label', 'odd', 'label', 'odd', 'label', 'odd'], transformProbabilities: transformData, title: props.categoryMarket.title, probabilities: filteredProbabilities, labelInButton: true, ...props }));
};

const K1TemplateMobile = ({ probabilities, renderName, ...props }) => {
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => probabilities.map(p => {
        return [
            {
                marketLabel: getOutcomeFullName(renderName, p, props.categoryMarket),
                ...p,
            },
        ];
    }), [props.categoryMarket, renderName]);
    return (React.createElement(OddsTableGroup, { columns: ['market'], transformProbabilities: transformData, probabilities: filteredProbabilities, ...props }));
};

const K2TemplateMobile = ({ probabilities, renderName, ...props }) => {
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        const sortedProbabilities = sortProbabilitiesByParamValue(probabilities);
        const matrix = listToMatrix(sortedProbabilities, 2);
        return matrix.map(row => row.map(p => {
            return {
                marketLabel: getOutcomeFullName(renderName, p, props.categoryMarket),
                ...p,
            };
        }));
    }, [props.categoryMarket, renderName]);
    return (React.createElement(OddsTableGroup, { columns: ['market', 'market'], transformProbabilities: transformData, probabilities: filteredProbabilities, ...props }));
};

const K3TemplateMobile = ({ probabilities, renderName, ...props }) => {
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        const sortedProbabilities = sortProbabilitiesByParamValue(probabilities);
        const matrix = listToMatrix(sortedProbabilities, 3);
        return matrix.map(row => row.map(p => {
            return {
                marketLabel: getOutcomeFullName(renderName, p, props.categoryMarket),
                ...p,
            };
        }));
    }, [props.categoryMarket, renderName]);
    return (React.createElement(OddsTableGroup, { columns: ['market', 'market', 'market'], transformProbabilities: transformData, probabilities: filteredProbabilities, ...props }));
};

const totalMarketLinks$2 = [MarketLink.TotalOver, MarketLink.TotalUnder];
const yesNoMarketLinks$2 = [MarketLink.Yes, MarketLink.No];
const G2TemplateMobile = ({ probabilities, renderName, ...props }) => {
    const { t } = useTranslation();
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        const byParameter = groupBy_1(probabilities, probability => {
            var _a;
            return Math.abs(+((_a = probability.parameters.find(p => p.type === ParameterType.Value)) === null || _a === void 0 ? void 0 : _a.value));
        });
        const sortedByTotalValue = Object.entries(byParameter).sort(([keyA, valueA], [keyB, valueB]) => {
            const isATotal = isInMarketLink(valueA, totalMarketLinks$2);
            const isBTotal = isInMarketLink(valueB, totalMarketLinks$2);
            if (isATotal && isBTotal) {
                return Number(keyA) - Number(keyB);
            }
            return 0;
        });
        const data = sortedByTotalValue.map(([, probs]) => {
            const isTotal = isInMarketLink(probs, totalMarketLinks$2);
            const isYesNo = isInMarketLink(probs, yesNoMarketLinks$2);
            const marketsRow = probs.map(prob => {
                let marketLabel = '';
                if (isTotal) {
                    marketLabel =
                        prob.outcomeTypeData.marketLink === MarketLink.TotalOver
                            ? t('over')
                            : t('under');
                }
                else if (isYesNo) {
                    marketLabel =
                        prob.outcomeTypeData.marketLink === MarketLink.Yes
                            ? t('yes')
                            : t('no');
                }
                return { marketLabel, ...prob };
            });
            marketsRow.sort(a => a.outcomeTypeData.marketLink === MarketLink.Yes ||
                a.outcomeTypeData.marketLink === MarketLink.TotalOver
                ? -1
                : 1);
            return [
                getOutcomeFullName(renderName, probs[0], props.categoryMarket),
                ...marketsRow,
            ];
        });
        return data;
    }, [props.categoryMarket, renderName, t]);
    return (React.createElement(OddsTableGroup, { transformProbabilities: transformData, columns: ['label', 'market', 'market'], probabilities: filteredProbabilities, ...props }));
};

const LK2TemplateMobile = ({ probabilities, renderName, ...props }) => {
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        const sortedProbabilities = sortProbabilitiesByParamValue(probabilities);
        const matrix = listToMatrix(sortedProbabilities, 2);
        return matrix.map(row => row.map(p => {
            return {
                marketLabel: getOutcomeFullName(renderName, p, props.categoryMarket),
                ...p,
            };
        }));
    }, [props.categoryMarket, renderName]);
    return (React.createElement(OddsTableGroup, { title: props.categoryMarket.title, columns: ['market', 'market'], transformProbabilities: transformData, probabilities: filteredProbabilities, ...props }));
};

const totalMarketLinks$3 = [MarketLink.TotalOver, MarketLink.TotalUnder];
const yesNoMarketLinks$3 = [MarketLink.Yes, MarketLink.No];
const LG2TemplateMobile = ({ probabilities, renderName, ...props }) => {
    const { t } = useTranslation();
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        const byParameter = groupBy_1(probabilities, probability => {
            var _a;
            return Math.abs(+((_a = probability.parameters.find(p => p.type === ParameterType.Value)) === null || _a === void 0 ? void 0 : _a.value));
        });
        const sortedByTotalValue = Object.entries(byParameter).sort(([keyA, valueA], [keyB, valueB]) => {
            const isATotal = isInMarketLink(valueA, totalMarketLinks$3);
            const isBTotal = isInMarketLink(valueB, totalMarketLinks$3);
            if (isATotal && isBTotal) {
                return Number(keyA) - Number(keyB);
            }
            return 0;
        });
        const data = sortedByTotalValue.map(([, probs]) => {
            const isTotal = isInMarketLink(probs, totalMarketLinks$3);
            const isYesNo = isInMarketLink(probs, yesNoMarketLinks$3);
            const marketsRow = probs.map(prob => {
                let marketLabel = '';
                if (isTotal) {
                    marketLabel =
                        prob.outcomeTypeData.marketLink === MarketLink.TotalOver
                            ? t('over')
                            : t('under');
                }
                else if (isYesNo) {
                    marketLabel =
                        prob.outcomeTypeData.marketLink === MarketLink.Yes
                            ? t('yes')
                            : t('no');
                }
                return { marketLabel, ...prob };
            });
            marketsRow.sort(a => a.outcomeTypeData.marketLink === MarketLink.Yes ||
                a.outcomeTypeData.marketLink === MarketLink.TotalOver
                ? -1
                : 1);
            return [
                getOutcomeFullName(renderName, probs[0], props.categoryMarket),
                ...marketsRow,
            ];
        });
        return data;
    }, [props.categoryMarket, renderName, t]);
    return (React.createElement(OddsTableGroup, { title: props.categoryMarket.title, transformProbabilities: transformData, columns: ['label', 'market', 'market'], probabilities: filteredProbabilities, ...props }));
};

const invertAwayProbabilityValueParameter$2 = (probability) => {
    const newProbability = { ...probability };
    if (probability.outcomeTypeData.marketLink === MarketLink.HandicapAway) {
        newProbability.parameters = newProbability.parameters.map(parameter => {
            if (parameter.type === ParameterType.Value) {
                const isInteger = parameter.value.split('.')[1] === '0';
                return {
                    type: parameter.type,
                    value: `${+parameter.value * -1}${isInteger ? '.0' : ''}`,
                };
            }
            else
                return parameter;
        });
    }
    return newProbability;
};
const LH2TemplateMobile = ({ probabilities, renderName, ...props }) => {
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        const invertHandicaps = probabilities.map(invertAwayProbabilityValueParameter$2);
        const groupsByValue = groupBy_1(invertHandicaps, probability => {
            var _a;
            return Number((_a = probability.parameters.find(p => p.type === ParameterType.Value)) === null || _a === void 0 ? void 0 : _a.value);
        });
        const rows = Object.values(groupsByValue)
            .sort((a, b) => Number(a) - Number(b))
            .map(group => {
            return group
                .map(invertAwayProbabilityValueParameter$2)
                .sort(a => {
                if (a.outcomeTypeData.marketLink === MarketLink.TotalOver) {
                    return -1;
                }
                return 0;
            })
                .map(p => ({
                marketLabel: getOutcomeFullName(renderName, p, props.categoryMarket),
                ...p,
            }));
        });
        return rows;
    }, [props.categoryMarket, renderName]);
    return (React.createElement(OddsTableGroup, { title: props.categoryMarket.title, transformProbabilities: transformData, columns: ['market', 'market'], probabilities: filteredProbabilities, ...props }));
};

const invertAwayProbabilityValueParameter$3 = (probability) => {
    const newProbability = { ...probability };
    if (probability.outcomeTypeData.marketLink === MarketLink.HandicapAway) {
        newProbability.parameters = newProbability.parameters.map(parameter => {
            if (parameter.type === ParameterType.Value) {
                const isInteger = parameter.value.split('.')[1] === '0';
                return {
                    type: parameter.type,
                    value: `${+parameter.value * -1}${isInteger ? '.0' : ''}`,
                };
            }
            else
                return parameter;
        });
    }
    return newProbability;
};
const H2TemplateMobile = ({ probabilities, renderName, ...props }) => {
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        const invertHandicaps = probabilities.map(invertAwayProbabilityValueParameter$3);
        const groupsByValue = groupBy_1(invertHandicaps, probability => {
            var _a;
            return Number((_a = probability.parameters.find(p => p.type === ParameterType.Value)) === null || _a === void 0 ? void 0 : _a.value);
        });
        const rows = Object.values(groupsByValue)
            .sort((a, b) => Number(a) - Number(b))
            .map(group => {
            return group
                .map(invertAwayProbabilityValueParameter$3)
                .sort(a => {
                if (a.outcomeTypeData.marketLink === MarketLink.TotalOver) {
                    return -1;
                }
                return 0;
            })
                .map(p => ({
                marketLabel: getOutcomeFullName(renderName, p, props.categoryMarket),
                ...p,
            }));
        });
        return rows;
    }, [props.categoryMarket, renderName]);
    return (React.createElement(OddsTableGroup, { transformProbabilities: transformData, columns: ['market', 'market'], probabilities: filteredProbabilities, ...props }));
};

const LK3TemplateMobile = ({ probabilities, renderName, ...props }) => {
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        const sortedProbabilities = sortProbabilitiesByParamValue(probabilities);
        const matrix = listToMatrix(sortedProbabilities, 3);
        return matrix.map(row => row.map(p => {
            return {
                marketLabel: getOutcomeFullName(renderName, p, props.categoryMarket),
                ...p,
            };
        }));
    }, [props.categoryMarket, renderName]);
    return (React.createElement(OddsTableGroup, { title: props.categoryMarket.title, columns: ['market', 'market', 'market'], transformProbabilities: transformData, probabilities: filteredProbabilities, ...props }));
};

const SK2TemplateMobile = ({ probabilities, renderName, ...props }) => {
    const { t } = useTranslation();
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        var _a, _b, _c;
        const { anyOtherOutcome, probabilitiesWithoutAnyOther } = extractAnyOtherOutcome(probabilities);
        const groupedProbabilities = groupBy_1(probabilitiesWithoutAnyOther, groupByScore);
        const rows = Object.values(mapValues_1(groupedProbabilities, (groupsProbabilities, group) => groupsProbabilities.sort(sortByGroup(group))));
        if (anyOtherOutcome) {
            const columnToPush = ((_a = rows[0]) === null || _a === void 0 ? void 0 : _a.length) <= ((_b = rows[1]) === null || _b === void 0 ? void 0 : _b.length) ? 0 : 1;
            const anyOtherOutcomeUpdated = {
                ...anyOtherOutcome,
                outcomeTypeData: {
                    ...anyOtherOutcome.outcomeTypeData,
                    name: t('anyOther')
                }
            };
            (_c = rows[columnToPush]) === null || _c === void 0 ? void 0 : _c.push(anyOtherOutcomeUpdated);
        }
        const maxLength = Math.max(...rows.map(group => group.length));
        return range_1(0, maxLength).map(rowNumber => rows.map(group => {
            const probability = group[rowNumber];
            if (probability) {
                return {
                    marketLabel: getOutcomeFullName(renderName, probability, props.categoryMarket),
                    ...probability,
                };
            }
            else {
                return null;
            }
        }));
    }, [props.categoryMarket, renderName, t]);
    return (React.createElement(OddsTableGroup, { columns: ['market', 'market'], transformProbabilities: transformData, title: props.categoryMarket.title, probabilities: filteredProbabilities, ...props }));
};

const SK3TemplateMobile = ({ probabilities, renderName, ...props }) => {
    const { t } = useTranslation();
    const filteredProbabilities = useFilteredProbabilities(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        const { anyOtherOutcome, probabilitiesWithoutAnyOther } = extractAnyOtherOutcome(probabilities);
        const rows = Object.values({
            ...{
                0: [],
                1: [],
                2: [],
            },
            ...mapValues_1(groupBy_1(probabilitiesWithoutAnyOther, groupByScore), (groupsProbabilities, group) => groupsProbabilities.sort(sortByGroup(group))),
        });
        if (anyOtherOutcome) {
            const anyOtherOutcomeUpdated = {
                ...anyOtherOutcome,
                outcomeTypeData: {
                    ...anyOtherOutcome.outcomeTypeData,
                    name: t('anyOther')
                }
            };
            rows[1].push(anyOtherOutcomeUpdated);
        }
        const maxLength = Math.max(...rows.map(group => group.length));
        return range_1(0, maxLength).map(rowNumber => rows.map(group => {
            const probability = group[rowNumber];
            if (probability) {
                return {
                    marketLabel: getOutcomeFullName(renderName, probability, props.categoryMarket),
                    ...probability,
                };
            }
            else {
                return null;
            }
        }));
    }, [props.categoryMarket, renderName, t]);
    return (React.createElement(OddsTableGroup, { columns: ['market', 'market', 'market'], transformProbabilities: transformData, title: props.categoryMarket.title, probabilities: filteredProbabilities, ...props }));
};

const OddsTableGroup$1 = ({ probabilities, categoryMarket, transformProbabilities, OddsTableComponent, ...props }) => {
    const { probabilitiesGroup, uniqParamCombinations } = useMemo(() => ({
        probabilitiesGroup: getProbabilitiesGroup(probabilities, categoryMarket),
        uniqParamCombinations: getUniqParamCombinations(probabilities, categoryMarket),
    }), [probabilities, categoryMarket]);
    return (React.createElement(React.Fragment, null, uniqParamCombinations.map((paramsCombination, index) => (React.createElement(OddsTableComponent
    // eslint-disable-next-line react/no-array-index-key
    , { 
        // eslint-disable-next-line react/no-array-index-key
        key: `ParamsCombination-${index}-${paramsCombination}`, rows: transformProbabilities(probabilitiesGroup[paramsCombination]), probabilities: probabilitiesGroup[paramsCombination], categoryMarket: categoryMarket, ...props })))));
};

const K1Template$1 = ({ probabilities, ...props }) => {
    const transformData = useCallback((probabilities) => probabilities.map(p => [p.outcomeTypeData.name, p]), []);
    return (React.createElement(OddsTableGroup$1, { columns: ['label', 'odd'], transformProbabilities: transformData, probabilities: probabilities, labelInButton: true, ...props }));
};

const getProbabilitiesMatrix = (probabilities, n) => {
    const matrix = listToMatrix(probabilities, n);
    const data = [];
    matrix.forEach(probabilitiesRow => {
        const row = [];
        probabilitiesRow.forEach(probability => {
            row.push(probability.outcomeTypeData.name);
            row.push(probability);
        });
        data.push(row);
    });
    return data;
};

const getK2Rows = (probabilities) => {
    return getProbabilitiesMatrix(probabilities, 2);
};

const getK3Rows = (probabilities) => {
    return getProbabilitiesMatrix(probabilities, 3);
};

const K2Template$1 = ({ probabilities, ...props }) => {
    const transformData = useCallback((probabilities) => {
        const rows = getK2Rows(probabilities);
        if (probabilities.length === 1) {
            sparseArrayK2(probabilities, rows);
        }
        return rows;
    }, []);
    return (React.createElement(OddsTableGroup$1, { columns: ['label', 'odd', 'label', 'odd'], transformProbabilities: transformData, probabilities: probabilities, labelInButton: true, ...props }));
};

const LK2Template$1 = ({ probabilities, ...props }) => {
    const transformData = useCallback((probabilities) => getK2Rows(probabilities), []);
    return (React.createElement(OddsTableGroup$1, { title: props.categoryMarket.outcomesGroup.name, columns: ['label', 'odd', 'label', 'odd'], transformProbabilities: transformData, probabilities: probabilities, labelInButton: true, ...props }));
};

var ScoreGroupsEnum$1;
(function (ScoreGroupsEnum) {
    ScoreGroupsEnum["HOME"] = "0";
    ScoreGroupsEnum["DRAW"] = "1";
    ScoreGroupsEnum["AWAY"] = "2";
})(ScoreGroupsEnum$1 || (ScoreGroupsEnum$1 = {}));
// TODO write tests
const getSKColumnsMap = (probabilities, sort) => {
    return mapValues_1(groupBy_1(probabilities, groupByScore), (groupsProbabilities, group) => groupsProbabilities.sort(sortByGroup(group, sort)));
};

// TODO write tests
const getSKRows = (columns) => {
    const maxLength = Math.max(...columns.map(group => group.length));
    const rows = [];
    range_1(0, maxLength).forEach(rowNumber => {
        const row = [];
        columns.forEach(group => {
            const probability = group[rowNumber];
            if (probability) {
                row.push(probability.outcomeTypeData.name);
                row.push(probability);
            }
            else {
                row.push('');
                row.push('');
            }
        });
        rows.push(row);
    });
    return rows;
};

// TODO write tests
const getCommonSort = (categoryMarket) => {
    var _a, _b;
    let sort = OrderDirection.Asc;
    const outcomeTypeOrderedBy = (_b = (_a = categoryMarket.outcomesGroup) === null || _a === void 0 ? void 0 : _a.orderedBy) === null || _b === void 0 ? void 0 : _b.outcomeTypeOrderedBy;
    if (outcomeTypeOrderedBy) {
        const schema = (outcomeTypeOrderedBy === null || outcomeTypeOrderedBy === void 0 ? void 0 : outcomeTypeOrderedBy.schema) || '';
        if (schema === 'CommonOutcomeTypeOrders') {
            const items = outcomeTypeOrderedBy.items;
            const sortRule = items.find(i => i.parameterType === ParameterType.Value);
            if (sortRule) {
                sort = sortRule.direction;
            }
        }
        else if (schema === 'OutcomeTypeSpecificOrders') {
            const items = outcomeTypeOrderedBy.items;
            let direction;
            const allItemsHasSimilarDirection = items.every(item => {
                var _a;
                const itemDirection = (_a = item.orderedBy.find(i => i.parameterType === ParameterType.Value)) === null || _a === void 0 ? void 0 : _a.direction;
                if (direction === undefined) {
                    direction = itemDirection;
                    return true;
                }
                return itemDirection === direction;
            });
            if (allItemsHasSimilarDirection) {
                sort = direction;
            }
        }
    }
    return sort;
};

const SK2Template$1 = ({ probabilities, ...props }) => {
    const { t } = useTranslation();
    const transformData = useCallback((probabilities) => {
        var _a, _b, _c;
        const { anyOtherOutcome, probabilitiesWithoutAnyOther } = extractAnyOtherOutcome(probabilities);
        const sort = getCommonSort(props.categoryMarket);
        const columns = Object.values({
            ...{
                0: [],
                2: [],
            },
            ...getSKColumnsMap(probabilitiesWithoutAnyOther, sort),
        });
        if (anyOtherOutcome) {
            const columnToPush = ((_a = columns[0]) === null || _a === void 0 ? void 0 : _a.length) <= ((_b = columns[1]) === null || _b === void 0 ? void 0 : _b.length) ? 0 : 1;
            const anyOtherOutcomeUpdated = {
                ...anyOtherOutcome,
                outcomeTypeData: {
                    ...anyOtherOutcome.outcomeTypeData,
                    name: t('anyOther'),
                },
            };
            (_c = columns[columnToPush]) === null || _c === void 0 ? void 0 : _c.push(anyOtherOutcomeUpdated);
        }
        return getSKRows(columns);
    }, [props.categoryMarket, t]);
    return (React.createElement(OddsTableGroup$1, { columns: ['label', 'odd', 'label', 'odd'], transformProbabilities: transformData, title: props.categoryMarket.outcomesGroup.name, probabilities: probabilities, ...props }));
};

// TODO write tests
const getSortInfo = (categoryMarket, firstColumnOutcomeTypeId) => {
    var _a, _b;
    let sort = OrderDirection.Asc;
    let isOppositeValue = true;
    const outcomeTypeOrderedBy = (_b = (_a = categoryMarket.outcomesGroup) === null || _a === void 0 ? void 0 : _a.orderedBy) === null || _b === void 0 ? void 0 : _b.outcomeTypeOrderedBy;
    if (outcomeTypeOrderedBy) {
        const schema = (outcomeTypeOrderedBy === null || outcomeTypeOrderedBy === void 0 ? void 0 : outcomeTypeOrderedBy.schema) || '';
        if (schema === 'CommonOutcomeTypeOrders') {
            const items = outcomeTypeOrderedBy.items;
            const sortRule = items.find(i => i.parameterType === ParameterType.Value);
            if (sortRule) {
                sort = sortRule.direction;
                isOppositeValue = true;
            }
        }
        if (schema === 'OutcomeTypeSpecificOrders') {
            const items = outcomeTypeOrderedBy.items;
            const firstColumnOrder = items.find(({ outcomeTypeId }) => outcomeTypeId === firstColumnOutcomeTypeId);
            if (firstColumnOrder) {
                const firstColumnSortRule = firstColumnOrder.orderedBy.find(({ parameterType }) => parameterType === ParameterType.Value);
                if (firstColumnSortRule) {
                    sort = firstColumnSortRule.direction;
                    const secondColumnOrder = items.find(({ outcomeTypeId }) => outcomeTypeId !== firstColumnOutcomeTypeId);
                    if (secondColumnOrder) {
                        const secondColumnSortRule = secondColumnOrder.orderedBy.find(({ parameterType }) => parameterType === ParameterType.Value);
                        if (secondColumnSortRule) {
                            isOppositeValue =
                                firstColumnSortRule.direction !==
                                    secondColumnSortRule.direction;
                        }
                    }
                }
            }
        }
    }
    return {
        sort,
        isOppositeValue,
    };
};

// TODO write tests
const groupByValue = (probabilities, firstColumnMarketLink, isOppositeValue) => {
    const byValue = {};
    probabilities.forEach(p => {
        const param = p.parameters.find(p => p.type === ParameterType.Value);
        if (param && param.value && Number.isFinite(+param.value)) {
            const value = +param.value;
            if (p.outcomeTypeData.marketLink === firstColumnMarketLink) {
                const group = byValue[value];
                if (group) {
                    byValue[value] = [p, group[1]];
                }
                else {
                    byValue[value] = [p, null];
                }
            }
            else {
                const value1 = isOppositeValue ? -value : value;
                const group = byValue[value1];
                if (group) {
                    byValue[value1] = [group[0], p];
                }
                else {
                    byValue[value1] = [null, p];
                }
            }
        }
    });
    return byValue;
};

const handicapMarketLinks = [
    MarketLink.HandicapHome,
    MarketLink.HandicapAway,
];

// TODO write tests
const getFirstColumnInfo = (probabilities, market) => {
    var _a, _b, _c, _d;
    let marketLink = MarketLink.HandicapHome;
    let firstColumnOutcomeTypeId = (_a = probabilities[0]) === null || _a === void 0 ? void 0 : _a.outcomeTypeId;
    const isHandicap = isInMarketLink(probabilities, handicapMarketLinks);
    if (isHandicap) {
        const orderedOutcomeTypeIds = (_c = (_b = market === null || market === void 0 ? void 0 : market.outcomesGroup) === null || _b === void 0 ? void 0 : _b.orderedBy) === null || _c === void 0 ? void 0 : _c.orderedOutcomeTypeIds;
        if (orderedOutcomeTypeIds && orderedOutcomeTypeIds.length > 0) {
            [firstColumnOutcomeTypeId] = orderedOutcomeTypeIds;
            const probability = probabilities.find(({ outcomeTypeId }) => outcomeTypeId === firstColumnOutcomeTypeId);
            if (probability && probability.outcomeTypeData.marketLink) {
                marketLink = probability.outcomeTypeData.marketLink;
            }
            if (probability === undefined) {
                const oppositeProbabilityMarketLink = (_d = probabilities[0]) === null || _d === void 0 ? void 0 : _d.outcomeTypeData.marketLink;
                if (oppositeProbabilityMarketLink === MarketLink.HandicapAway) {
                    marketLink = MarketLink.HandicapHome;
                }
                else if (oppositeProbabilityMarketLink === MarketLink.HandicapHome) {
                    marketLink = MarketLink.HandicapAway;
                }
            }
        }
    }
    return {
        marketLink,
        outcomeTypeId: firstColumnOutcomeTypeId,
    };
};

// TODO write tests
const getH2Rows = (groupsByValue, sort) => {
    return Object.keys(groupsByValue)
        .sort((v1, v2) => sort === OrderDirection.Asc
        ? Number(v1) - Number(v2)
        : Number(v2) - Number(v1))
        .map(v => {
        const probsByValue = groupsByValue[Number(v)];
        return [
            probsByValue[0] ? probsByValue[0].outcomeTypeData.name : '',
            probsByValue[0] ? probsByValue[0] : '',
            probsByValue[1] ? probsByValue[1].outcomeTypeData.name : '',
            probsByValue[1] ? probsByValue[1] : '',
        ];
    });
};

const H2Template$1 = ({ probabilities, ...props }) => {
    const { competitors, isMobile, isPlain, categoryMarket } = props;
    const { marketLink, outcomeTypeId } = getFirstColumnInfo(probabilities, categoryMarket);
    const transformData = useCallback((probabilities) => {
        const { sort, isOppositeValue } = getSortInfo(categoryMarket, outcomeTypeId);
        const groupsByValue = groupByValue(probabilities, marketLink, isOppositeValue);
        return getH2Rows(groupsByValue, sort);
    }, [categoryMarket, outcomeTypeId, marketLink]);
    const titles = useMemo(() => {
        if (!isMobile || isPlain) {
            return [''];
        }
        const homeCompetitor = competitors.homeCompetitors
            .map(a => a.name)
            .join(',');
        const awayCompetitor = competitors.awayCompetitors
            .map(a => a.name)
            .join(',');
        return marketLink === MarketLink.HandicapAway
            ? [[awayCompetitor, '', homeCompetitor, '']]
            : [[homeCompetitor, '', awayCompetitor, '']];
    }, [isMobile, isPlain, competitors, marketLink]);
    return (React.createElement(OddsTableGroup$1, { transformProbabilities: transformData, title: titles, columns: ['label', 'odd', 'label', 'odd'], probabilities: probabilities, labelInButton: true, ...props }));
};

const LH2Template$1 = ({ probabilities, ...props }) => {
    const { competitors, isMobile, isPlain, categoryMarket } = props;
    const { marketLink, outcomeTypeId } = getFirstColumnInfo(probabilities, categoryMarket);
    const transformData = useCallback((probabilities) => {
        const { sort, isOppositeValue } = getSortInfo(categoryMarket, outcomeTypeId);
        const groupsByValue = groupByValue(probabilities, marketLink, isOppositeValue);
        return getH2Rows(groupsByValue, sort);
    }, [categoryMarket, outcomeTypeId, marketLink]);
    const titles = useMemo(() => {
        if (!isMobile || isPlain) {
            return [[categoryMarket.outcomesGroup.name]];
        }
        const homeCompetitor = competitors.homeCompetitors
            .map(a => a.name)
            .join(',');
        const awayCompetitor = competitors.awayCompetitors
            .map(a => a.name)
            .join(',');
        const titles = marketLink === MarketLink.HandicapAway
            ? [awayCompetitor, '', homeCompetitor, '']
            : [homeCompetitor, '', awayCompetitor, ''];
        return [[categoryMarket.outcomesGroup.name], titles];
    }, [categoryMarket, competitors, isMobile, isPlain, marketLink]);
    return (React.createElement(OddsTableGroup$1, { transformProbabilities: transformData, title: titles, columns: ['label', 'odd', 'label', 'odd'], probabilities: probabilities, labelInButton: true, ...props }));
};

// TODO write tests
const groupByValue$1 = (probabilities, firstColumnMarketLink) => {
    const groupsByValue = {};
    probabilities.forEach(probability => {
        var _a;
        const value = Math.abs(+((_a = probability.parameters.find(p => p.type === ParameterType.Value)) === null || _a === void 0 ? void 0 : _a.value));
        if (probability.outcomeTypeData.marketLink === firstColumnMarketLink) {
            const group = groupsByValue[value];
            if (group) {
                groupsByValue[value] = [probability, group[1]];
            }
            else {
                groupsByValue[value] = [probability, null];
            }
        }
        else {
            const group = groupsByValue[value];
            if (group) {
                groupsByValue[value] = [group[0], probability];
            }
            else {
                groupsByValue[value] = [null, probability];
            }
        }
    });
    return groupsByValue;
};

const totalMarketLinks$4 = [MarketLink.TotalOver, MarketLink.TotalUnder];
// TODO write tests
const getG2Rows = (groupsByValue, sort) => {
    return Object.keys(groupsByValue)
        .sort((v1, v2) => {
        const isATotal = isInMarketLink(groupsByValue[v1], totalMarketLinks$4);
        const isBTotal = isInMarketLink(groupsByValue[v2], totalMarketLinks$4);
        if (isATotal && isBTotal) {
            return sort === OrderDirection.Asc
                ? Number(v1) - Number(v2)
                : Number(v2) - Number(v1);
        }
        return 0;
    })
        .map(v => {
        const probsByValue = groupsByValue[Number(v)];
        let outcomeName = '';
        if (probsByValue[0]) {
            outcomeName = probsByValue[0].outcomeTypeData.name;
        }
        else if (probsByValue[1]) {
            outcomeName = probsByValue[1].outcomeTypeData.name;
        }
        return [
            outcomeName,
            probsByValue[0] ? probsByValue[0] : '',
            probsByValue[1] ? probsByValue[1] : '',
        ];
    });
};

// TODO write tests
const getG2Titles = (firstColumnMarketLink, t) => {
    if (firstColumnMarketLink === MarketLink.TotalOver) {
        return [t('over'), t('under')];
    }
    if (firstColumnMarketLink === MarketLink.TotalUnder) {
        return [t('under'), t('over')];
    }
    if (firstColumnMarketLink === MarketLink.Yes) {
        return [t('yes'), t('no')];
    }
    if (firstColumnMarketLink === MarketLink.No) {
        return [t('no'), t('yes')];
    }
    return [];
};

const totalMarketLinks$5 = [MarketLink.TotalOver, MarketLink.TotalUnder];
const yesNoMarketLinks$4 = [MarketLink.Yes, MarketLink.No];

// TODO write tests
const getFirstColumnMarketLink = (probabilities, market) => {
    var _a, _b, _c, _d;
    const orderedOutcomeTypeIds = (_b = (_a = market === null || market === void 0 ? void 0 : market.outcomesGroup) === null || _a === void 0 ? void 0 : _a.orderedBy) === null || _b === void 0 ? void 0 : _b.orderedOutcomeTypeIds;
    const isTotal = isInMarketLink(probabilities, totalMarketLinks$5);
    if (isTotal) {
        const marketLink = MarketLink.TotalOver;
        if (orderedOutcomeTypeIds && orderedOutcomeTypeIds.length > 0) {
            const probability = probabilities.find(({ outcomeTypeId }) => outcomeTypeId === orderedOutcomeTypeIds[0]);
            if (probability && probability.outcomeTypeData.marketLink) {
                return probability.outcomeTypeData.marketLink;
            }
            if (probability === undefined) {
                const oppositeProbabilityMarketLink = (_c = probabilities[0]) === null || _c === void 0 ? void 0 : _c.outcomeTypeData.marketLink;
                if (oppositeProbabilityMarketLink === MarketLink.TotalUnder) {
                    return MarketLink.TotalOver;
                }
                else if (oppositeProbabilityMarketLink === MarketLink.TotalOver) {
                    return MarketLink.TotalUnder;
                }
            }
        }
        return marketLink;
    }
    const isYesNo = isInMarketLink(probabilities, yesNoMarketLinks$4);
    if (isYesNo) {
        const marketLink = MarketLink.Yes;
        if (orderedOutcomeTypeIds && orderedOutcomeTypeIds.length > 0) {
            const probability = probabilities.find(({ outcomeTypeId }) => outcomeTypeId === orderedOutcomeTypeIds[0]);
            if (probability && probability.outcomeTypeData.marketLink) {
                return probability.outcomeTypeData.marketLink;
            }
            if (probability === undefined) {
                const oppositeProbabilityMarketLink = (_d = probabilities[0]) === null || _d === void 0 ? void 0 : _d.outcomeTypeData.marketLink;
                if (oppositeProbabilityMarketLink === MarketLink.No) {
                    return MarketLink.Yes;
                }
                else if (oppositeProbabilityMarketLink === MarketLink.Yes) {
                    return MarketLink.No;
                }
            }
        }
        return marketLink;
    }
    return MarketLink.Any;
};

const G2Template$1 = ({ probabilities, ...props }) => {
    const { t } = useTranslation();
    const firstColumnMarketLink = getFirstColumnMarketLink(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        const sort = getCommonSort(props.categoryMarket);
        const groupsByValue = groupByValue$1(probabilities, firstColumnMarketLink);
        return getG2Rows(groupsByValue, sort);
    }, [props.categoryMarket, firstColumnMarketLink]);
    const titles = useMemo(() => {
        if (props.isPlain) {
            return [];
        }
        return getG2Titles(firstColumnMarketLink, t);
    }, [firstColumnMarketLink, props.isPlain, t]);
    return (React.createElement(OddsTableGroup$1, { transformProbabilities: transformData, title: ['', ...titles], columns: ['label', 'odd', 'odd'], probabilities: probabilities, ...props }));
};

const LG2Template$1 = ({ probabilities, ...props }) => {
    const { t } = useTranslation();
    const firstColumnMarketLink = getFirstColumnMarketLink(probabilities, props.categoryMarket);
    const transformData = useCallback((probabilities) => {
        const sort = getCommonSort(props.categoryMarket);
        const groupsByValue = groupByValue$1(probabilities, firstColumnMarketLink);
        return getG2Rows(groupsByValue, sort);
    }, [props.categoryMarket, firstColumnMarketLink]);
    const titles = useMemo(() => {
        if (props.isPlain) {
            return [];
        }
        return getG2Titles(firstColumnMarketLink, t);
    }, [firstColumnMarketLink, props.isPlain, t]);
    return (React.createElement(OddsTableGroup$1, { transformProbabilities: transformData, title: [props.categoryMarket.outcomesGroup.name, ...titles], columns: ['label', 'odd', 'odd'], probabilities: probabilities, ...props }));
};

const K3Template$1 = ({ probabilities, ...props }) => {
    const transformData = useCallback((probabilities) => {
        const rows = getK3Rows(probabilities);
        if (probabilities.length < 3) {
            sparseArrayK3(probabilities, rows);
        }
        return rows;
    }, []);
    return (React.createElement(OddsTableGroup$1, { columns: ['label', 'odd', 'label', 'odd', 'label', 'odd'], transformProbabilities: transformData, probabilities: probabilities, labelInButton: true, ...props }));
};

const LK3Template$1 = ({ probabilities, ...props }) => {
    const transformData = useCallback((probabilities) => getK3Rows(probabilities), []);
    return (React.createElement(OddsTableGroup$1, { title: props.categoryMarket.outcomesGroup.name, columns: ['label', 'odd', 'label', 'odd', 'label', 'odd'], transformProbabilities: transformData, probabilities: probabilities, labelInButton: true, ...props }));
};

const SK3Template$1 = ({ probabilities, ...props }) => {
    const { t } = useTranslation();
    const transformData = useCallback((probabilities) => {
        const { anyOtherOutcome, probabilitiesWithoutAnyOther } = extractAnyOtherOutcome(probabilities);
        const sort = getCommonSort(props.categoryMarket);
        const columns = Object.values({
            ...{
                0: [],
                1: [],
                2: [],
            },
            ...getSKColumnsMap(probabilitiesWithoutAnyOther, sort),
        });
        if (anyOtherOutcome) {
            const anyOtherOutcomeUpdated = {
                ...anyOtherOutcome,
                outcomeTypeData: {
                    ...anyOtherOutcome.outcomeTypeData,
                    name: t('anyOther'),
                },
            };
            columns[1].push(anyOtherOutcomeUpdated);
        }
        return getSKRows(columns);
    }, [props.categoryMarket, t]);
    return (React.createElement(OddsTableGroup$1, { columns: ['label', 'odd', 'label', 'odd', 'label', 'odd'], transformProbabilities: transformData, title: props.categoryMarket.outcomesGroup.name, probabilities: probabilities, labelInButton: true, ...props }));
};

export { G2Template, G2TemplateMobile, G2Template$1 as G2TemplateNew, H2Template, H2TemplateMobile, H2Template$1 as H2TemplateNew, K1Template, K1TemplateMobile, K1Template$1 as K1TemplateNew, K2Template, K2TemplateMobile, K2Template$1 as K2TemplateNew, K3Template, K3TemplateMobile, K3Template$1 as K3TemplateNew, LG2Template, LG2TemplateMobile, LG2Template$1 as LG2TemplateNew, LH2Template, LH2TemplateMobile, LH2Template$1 as LH2TemplateNew, LK2Template, LK2TemplateMobile, LK2Template$1 as LK2TemplateNew, LK3Template, LK3TemplateMobile, LK3Template$1 as LK3TemplateNew, SK2Template, SK2TemplateMobile, SK2Template$1 as SK2TemplateNew, SK3Template, SK3TemplateMobile, SK3Template$1 as SK3TemplateNew };
//# sourceMappingURL=index.esm.js.map
